import { useEffect, useState, useCallback, useRef } from "react";
import { motion } from "framer-motion";
import {
  Loader2, PlayCircle, RefreshCw, Search, Settings2, Send, Sparkles, Mail,
  Link2, CheckCircle2, AlertTriangle, ChevronDown, ChevronRight,
  CalendarCheck, Reply, ThumbsUp, Trophy, Ban, Save, Radar, FileSearch, Clock,
  MessageSquare, Smartphone, Zap, Phone, Inbox, BarChart3, Send as SendIcon, Award,
  CheckSquare, Square, Copy, Check, FileText,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

const RATING_OPTIONS = ["A+", "A", "B", "C+", "C", "C-", "D+", "D", "D-", "F", "NR"];

const STATUS_META = {
  found: { label: "Found", color: "text-[#1e3d3a]/60 bg-[#1e3d3a]/6 border-[#1e3d3a]/12" },
  analyzed: { label: "Analyzed", color: "text-[#4d5a24] bg-[#6b7c3a]/10 border-[#6b7c3a]/30" },
  drafted: { label: "Drafted", color: "text-[#a3823f] bg-[#c9a961]/12 border-[#c9a961]/30" },
  queued: { label: "Ready to send", color: "text-[#a3823f] bg-[#c9a961]/14 border-[#c9a961]/40" },
  needs_email: { label: "Needs email", color: "text-[#b23a48] bg-[#b23a48]/8 border-[#b23a48]/30" },
  sent: { label: "Sent", color: "text-[#1e3d3a] bg-[#1e3d3a]/8 border-[#1e3d3a]/20" },
  replied: { label: "Replied", color: "text-[#4d5a24] bg-[#6b7c3a]/12 border-[#6b7c3a]/40" },
  positive_reply: { label: "Positive reply", color: "text-white bg-[#6b7c3a] border-[#6b7c3a]" },
  meeting: { label: "Meeting", color: "text-white bg-[#1e3d3a] border-[#1e3d3a]" },
  converted: { label: "Converted", color: "text-white bg-[#a3823f] border-[#a3823f]" },
  opted_out: { label: "Opted out", color: "text-[#b23a48] bg-[#b23a48]/8 border-[#b23a48]/30" },
  dismissed: { label: "Dismissed", color: "text-[#1e3d3a]/45 bg-[#1e3d3a]/5 border-[#1e3d3a]/12" },
};

const scoreColor = (s) =>
  s >= 70 ? "text-[#4d5a24] bg-[#6b7c3a]/14 border-[#6b7c3a]/40"
    : s >= 45 ? "text-[#a3823f] bg-[#c9a961]/14 border-[#c9a961]/40"
      : "text-[#b23a48] bg-[#b23a48]/8 border-[#b23a48]/30";

const FUNNEL = [
  { key: "found", label: "Found", icon: Radar, filter: "all" },
  { key: "queued", label: "Ready", icon: Mail, filter: "queued" },
  { key: "sent", label: "Sent", icon: Send, filter: "sent,replied,positive_reply,meeting,converted,opted_out" },
  { key: "delivered", label: "Delivered", icon: CheckCircle2, filter: "sent,replied,positive_reply,meeting,converted,opted_out" },
  { key: "replies", label: "Replies", icon: Reply, filter: "replied,positive_reply,meeting,converted,opted_out" },
  { key: "positive_replies", label: "Positive", icon: ThumbsUp, filter: "positive_reply,meeting,converted" },
  { key: "meetings", label: "Meetings", icon: CalendarCheck, filter: "meeting,converted" },
  { key: "converted", label: "Converted", icon: Trophy, filter: "converted" },
];

export default function OutreachEngine({ onResearch }) {
  const [config, setConfig] = useState(null);
  const [founders, setFounders] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [ownerFilter, setOwnerFilter] = useState("all");
  const [gmail, setGmail] = useState({ connected: false });
  const [funnel, setFunnel] = useState({});
  const [leads, setLeads] = useState([]);
  const [statusFilter, setStatusFilter] = useState(() => {
    try { return new URLSearchParams(window.location.search).get("leads") || "all"; } catch { return "all"; }
  });
  const leadsListRef = useRef(null);
  const focusLeads = useCallback((filter) => {
    setStatusFilter(filter);
    setOwnerFilter("all");
    try {
      const p = new URLSearchParams(window.location.search);
      p.set("leads", filter);
      window.history.replaceState({}, "", window.location.pathname + "?" + p.toString());
    } catch { /* ignore */ }
    setTimeout(() => leadsListRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
  }, []);
  // If deep-linked via ?leads=<filter>, scroll the leads list into view on load.
  useEffect(() => {
    try {
      if (new URLSearchParams(window.location.search).get("leads")) {
        setOwnerFilter("all");
        setTimeout(() => leadsListRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 600);
      }
    } catch { /* ignore */ }
  }, []);
  const [showConfig, setShowConfig] = useState(false);
  const [running, setRunning] = useState(false);
  const [runLimit, setRunLimit] = useState(25);
  const [finding, setFinding] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [savingCfg, setSavingCfg] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [costPreview, setCostPreview] = useState(null);
  const [collapsed, setCollapsed] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("elevare_outreach_collapsed") || "{}");
      return { smsLib: true, ...saved };  // template library starts collapsed
    } catch { return { smsLib: true }; }
  });
  const toggleCollapse = (key) => setCollapsed((c) => {
    const next = { ...c, [key]: !c[key] };
    try { localStorage.setItem("elevare_outreach_collapsed", JSON.stringify(next)); } catch { /* ignore */ }
    return next;
  });
  const [find, setFind] = useState({ keyword: "", location: "", cities: "", radius_mi: 0, ratings: [], source: "both", min_reviews: 0, min_rating: 0, include_categories: "", exclude_categories: "" });
  const pollRef = useRef(null);
  const [jobProgress, setJobProgress] = useState(null);
  const [activeJobId, setActiveJobId] = useState(null);
  const [stopping, setStopping] = useState(false);
  const [lastRunCost, setLastRunCost] = useState(null);
  const [presets, setPresets] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [presetName, setPresetName] = useState("");
  const [showSavePreset, setShowSavePreset] = useState(false);
  const [scheduleEditor, setScheduleEditor] = useState(null); // preset id being scheduled
  const [smsStatus, setSmsStatus] = useState({ configured: false, from_number: "" });
  const [massSending, setMassSending] = useState(null); // "email" | "sms" | null
  const [showSchedule, setShowSchedule] = useState(false);
  const [schedNext, setSchedNext] = useState(null);
  const [showInbox, setShowInbox] = useState(false);
  const [inbox, setInbox] = useState({ conversations: [], sms_configured: false });
  const [activeId, setActiveId] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const loadInbox = useCallback(() => {
    api.get("/admin/outreach/inbox").then(({ data }) => setInbox(data)).catch(() => {});
  }, []);
  const loadAnalytics = useCallback(() => {
    api.get("/admin/outreach/analytics").then(({ data }) => setAnalytics(data)).catch(() => {});
  }, []);
  const openInbox = () => setShowInbox((v) => { const n = !v; if (n) { loadInbox(); setShowAnalytics(false); } return n; });
  const openAnalytics = () => setShowAnalytics((v) => { const n = !v; if (n) { loadAnalytics(); setShowInbox(false); } return n; });
  const sendReply = async () => {
    if (!activeId || !replyText.trim()) return;
    try {
      await api.post(`/admin/outreach/leads/${activeId}/sms/send`, { body: replyText });
      toast.success("Text sent.");
      setReplyText(""); loadInbox();
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed."); }
  };
  useEffect(() => {
    api.get("/admin/outreach/sms/status").then(({ data }) => setSmsStatus(data)).catch(() => {});
  }, []);

  const citiesArr = (s) => (s || "").split(/[\n,]/).map((x) => x.trim()).filter(Boolean);

  const FOUNDER_COLORS = ["#1e3d3a", "#a3823f", "#6b7c3a", "#b23a48"];
  const founderName = (u) => founders.find((f) => f.username === u)?.name || (u ? u[0].toUpperCase() + u.slice(1) : "Unassigned");
  const founderColor = (u) => { const i = founders.findIndex((f) => f.username === u); return i >= 0 ? FOUNDER_COLORS[i % FOUNDER_COLORS.length] : "#9a9a9a"; };
  const reassignOwner = async (leadId, owner) => {
    try {
      await api.patch(`/admin/outreach/leads/${leadId}`, { owner: owner || "" });
      toast.success(owner ? `Assigned to ${founderName(owner)}.` : "Owner cleared.");
      loadLeads();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not reassign."); }
  };
  const toggleSelect = (id) => setSelected((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const clearSelection = () => setSelected(new Set());
  const bulkAssign = async (owner, payload) => {
    setBulkBusy(true);
    try {
      const { data } = await api.post("/admin/outreach/leads/bulk-assign", { owner, ...payload });
      toast.success(`Assigned ${data.assigned} lead${data.assigned === 1 ? "" : "s"} to ${founderName(owner)}.`);
      clearSelection();
      loadLeads();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not assign leads."); }
    finally { setBulkBusy(false); }
  };
  const setGoal = async (username, value) => {
    const goals = { ...(config?.goals || {}), [username]: Math.max(0, parseInt(value, 10) || 0) };
    try {
      const { data } = await api.put("/admin/outreach/config", { goals });
      setConfig(data.config);
      toast.success("Goal saved.");
      loadAnalytics();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save goal."); }
  };

  const stopPolling = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  const clearBusy = () => { setRunning(false); setFinding(false); setSyncing(false); setJobProgress(null); setActiveJobId(null); };

  const loadPresets = useCallback(() => {
    api.get("/admin/outreach/presets").then(({ data }) => setPresets(data.presets || [])).catch(() => {});
    api.get("/admin/outreach/schedule/upcoming").then(({ data }) => setUpcoming(data.upcoming || [])).catch(() => {});
  }, []);

  const loadDash = useCallback(() => {
    api.get("/admin/outreach/dashboard").then(({ data }) => setFunnel(data.funnel || {})).catch(() => {});
  }, []);
  const loadLeads = useCallback(() => {
    api.get("/admin/outreach/leads", { params: { status: statusFilter } })
      .then(({ data }) => setLeads(data.leads || []))
      .catch(() => toast.error("Could not load outreach leads."));
  }, [statusFilter]);
  const loadConfig = useCallback(() => {
    api.get("/admin/outreach/config").then(({ data }) => {
      setConfig(data.config);
      setGmail(data.gmail || { connected: false });
      setFounders(data.founders || []);
      setCurrentUser(data.current_user || null);
      setFind((f) => ({ ...f, ratings: data.config?.icp_ratings || [], source: data.config?.lead_source || "both", min_reviews: data.config?.min_reviews || 0, min_rating: data.config?.min_rating || 0, radius_mi: data.config?.radius_mi || 0, include_categories: (data.config?.include_categories || []).join(", "), exclude_categories: (data.config?.exclude_categories || []).join(", ") }));
    }).catch(() => {});
  }, []);

  useEffect(() => { loadConfig(); loadDash(); }, [loadConfig, loadDash]);
  useEffect(() => { loadInbox(); }, [loadInbox]);
  useEffect(() => { loadLeads(); }, [loadLeads]);

  const connectGoogle = async () => {
    try {
      const { data } = await api.get("/admin/calendar/connect");
      window.location.href = data.authorization_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start Google connection.");
    }
  };

  const saveConfig = async (patch) => {
    setSavingCfg(true);
    try {
      const { data } = await api.put("/admin/outreach/config", patch);
      setConfig(data.config);
      toast.success("Settings saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save settings.");
    } finally { setSavingCfg(false); }
  };

  const fmtCost = (c) => (c ? `$${(c.total_low ?? 0).toFixed(2)}–$${(c.total_high ?? 0).toFixed(2)}` : null);
  const summarize = (job) => {
    const s = job.summary || {};
    const cost = fmtCost(s.cost);
    const suffix = cost ? ` · cost ${cost}` : "";
    if (job.kind === "find") return `Found ${s.found ?? 0} new prospect(s).${suffix}`;
    if (job.kind === "enrich") return `Enriched ${s.enriched ?? 0} lead(s) · found ${s.emails_found ?? 0} email(s).${suffix}`;
    if (job.kind === "sync") return `Synced ${s.replies_synced ?? 0} reply(ies) · ${s.positive_replies ?? 0} positive.`;
    return `Found ${s.found ?? 0} · analyzed ${s.analyzed ?? 0} · drafted ${s.drafted ?? 0}${s.sent ? ` · sent ${s.sent}` : ""}${suffix}`;
  };

  const startPolling = useCallback((jobId, label) => {
    stopPolling();
    setActiveJobId(jobId);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/admin/outreach/jobs/${jobId}`);
        const job = data.job;
        loadDash(); loadLeads();
        if (!job) return;
        if (job.progress) setJobProgress(job.progress);
        if (job.status === "done") {
          stopPolling(); clearBusy();
          if (job.summary?.cost) setLastRunCost({ kind: job.kind, cost: job.summary.cost, at: Date.now() });
          toast.success(summarize(job));
          loadConfig();
        } else if (job.status === "cancelled") {
          stopPolling(); clearBusy();
          if (job.summary?.cost) setLastRunCost({ kind: job.kind, cost: job.summary.cost, at: Date.now() });
          toast.message("Engine stopped." + (job.summary?.cost ? ` Spent so far ${fmtCost(job.summary.cost)}.` : ""));
          loadConfig();
        } else if (job.status === "error") {
          stopPolling(); clearBusy();
          toast.error(job.error || `${label} failed.`);
        }
      } catch (_) { /* keep polling */ }
    }, 2500);
  }, [loadDash, loadLeads, loadConfig]);

  const enrichMissing = async () => {
    if (pollRef.current) { toast.message("A job is already running…"); return; }
    try {
      const { data } = await api.post("/admin/outreach/enrich-missing");
      if (!data.job) { toast.message("No leads are missing emails right now."); return; }
      setRunning(true);
      toast.message(`Enriching ${data.count} lead(s) missing emails — scraping sites & finding decision-makers…`);
      startPolling(data.job.id, "Enrich");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start enrichment."); }
  };

  const stopEngine = async () => {
    setStopping(true);
    try {
      await api.post("/admin/outreach/jobs/cancel-active");
      stopPolling(); clearBusy();
      toast.message("Stopping the engine…");
      loadDash(); loadLeads();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not stop the engine.");
    } finally { setStopping(false); }
  };

  const runBodyBase = () => ({
    keyword: find.keyword, location: find.location, locations: citiesArr(find.cities), radius_mi: Number(find.radius_mi) || 0, ratings: find.ratings, source: find.source,
    min_reviews: Number(find.min_reviews) || 0, min_rating: Number(find.min_rating) || 0,
    include_categories: (find.include_categories || "").split(",").map((x) => x.trim()).filter(Boolean),
    exclude_categories: (find.exclude_categories || "").split(",").map((x) => x.trim()).filter(Boolean),
  });
  const nLeads = () => Math.max(1, Math.min(1000, Number(runLimit) || 25));

  // Rough per-unit estimates (USD). Apify compass/crawler-google-places is
  // ~$1.50–$4 per 1,000 places by plan (+ ~$0.002/place detail); gpt-5.4 runs
  // twice per new lead (analyze + draft). Ranges shown, dedupe lowers AI cost.
  const estimateCost = (withAI) => {
    const cities = Math.max(1, citiesArr(find.cities).length);
    const places = nLeads() * cities;
    const apifyLow = places * 0.004, apifyHigh = places * 0.007;
    const aiCalls = withAI ? places * 2 : 0;
    const aiLow = withAI ? places * 0.008 : 0, aiHigh = withAI ? places * 0.02 : 0;
    return { cities, places, apifyLow, apifyHigh, aiCalls, aiLow, aiHigh,
             totalLow: apifyLow + aiLow, totalHigh: apifyHigh + aiHigh };
  };
  const COST_CONFIRM_THRESHOLD = 100; // estimated places above which we confirm first

  const executeRun = async () => {
    if (pollRef.current) { toast.message("A job is already running…"); return; }
    const n = nLeads();
    setRunning(true);
    toast.message(`Engine running in the background — finding up to ${n}/city, analyzing & drafting. This can take a few minutes…`);
    try {
      const { data } = await api.post("/admin/outreach/run", { ...runBodyBase(), limit: n });
      if (data.job?.id) startPolling(data.job.id, "Engine run");
      else { setRunning(false); loadDash(); loadLeads(); }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Engine run failed.");
      setRunning(false);
    }
  };

  const executeFind = async () => {
    if (pollRef.current) { toast.message("A job is already running…"); return; }
    const n = nLeads();
    setFinding(true);
    toast.message("Searching Google Maps in the background — this can take a minute…");
    try {
      const { data } = await api.post("/admin/outreach/find", { ...runBodyBase(), limit: n });
      if (data.job?.id) startPolling(data.job.id, "Lead finder");
      else { setFinding(false); loadDash(); loadLeads(); }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Lead finder failed.");
      setFinding(false);
    }
  };

  const runEngine = () => {
    if (pollRef.current) { toast.message("A job is already running…"); return; }
    const est = estimateCost(true);
    if (est.places >= COST_CONFIRM_THRESHOLD) setCostPreview({ kind: "run", est });
    else executeRun();
  };
  const findLeads = () => {
    if (pollRef.current) { toast.message("A job is already running…"); return; }
    const est = estimateCost(false);
    if (est.places >= COST_CONFIRM_THRESHOLD) setCostPreview({ kind: "find", est });
    else executeFind();
  };
  const confirmCostPreview = () => {
    const p = costPreview; setCostPreview(null);
    if (!p) return;
    if (p.kind === "run") executeRun(); else executeFind();
  };

  const syncReplies = async () => {
    if (pollRef.current) { toast.message("A job is already running…"); return; }
    setSyncing(true);
    try {
      const { data } = await api.post("/admin/outreach/sync-replies");
      if (data.job?.id) startPolling(data.job.id, "Reply sync");
      else { setSyncing(false); loadDash(); loadLeads(); }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reply sync failed.");
      setSyncing(false);
    }
  };

  // Resume polling if a job is already running (e.g. after a refresh).
  useEffect(() => {
    let cancelled = false;
    api.get("/admin/outreach/jobs/active").then(({ data }) => {
      if (cancelled || !data.job) return;
      const k = data.job.kind;
      if (k === "find") setFinding(true);
      else if (k === "sync") setSyncing(true);
      else setRunning(true);
      if (data.job.progress) setJobProgress(data.job.progress);
      startPolling(data.job.id, "Job");
    }).catch(() => {});
    return () => { cancelled = true; stopPolling(); };
  }, [startPolling]);

  useEffect(() => { loadPresets(); }, [loadPresets]);

  const applyPreset = (p) => setFind({
    keyword: p.keyword || "", location: p.location || "",
    cities: (p.locations && p.locations.length > 1) ? p.locations.join(", ") : "",
    source: p.source || "both",
    ratings: p.ratings || [], min_reviews: p.min_reviews || 0, min_rating: p.min_rating || 0,
    include_categories: (p.include_categories || []).join(", "),
    exclude_categories: (p.exclude_categories || []).join(", "),
  });

  const firePreset = async (p) => {
    applyPreset(p);
    if (pollRef.current) { toast.message("A job is already running…"); return; }
    setRunning(true);
    setJobProgress({ phase: "finding" });
    toast.message(`Running preset "${p.name}" in the background…`);
    try {
      const { data } = await api.post(`/admin/outreach/presets/${p.id}/run`);
      if (data.job?.id) startPolling(data.job.id, "Preset run");
      else { setRunning(false); loadDash(); loadLeads(); }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preset run failed.");
      setRunning(false); setJobProgress(null);
    }
  };

  const savePreset = async () => {
    const name = presetName.trim();
    if (!name) { toast.error("Name your preset first."); return; }
    const cities = citiesArr(find.cities);
    try {
      await api.post("/admin/outreach/presets", {
        name, keyword: find.keyword, location: find.location,
        locations: cities.length ? cities : (find.location ? [find.location] : []),
        source: find.source, ratings: find.ratings, radius_mi: Number(find.radius_mi) || 0,
        min_reviews: Number(find.min_reviews) || 0, min_rating: Number(find.min_rating) || 0,
        include_categories: (find.include_categories || "").split(",").map((x) => x.trim()).filter(Boolean),
        exclude_categories: (find.exclude_categories || "").split(",").map((x) => x.trim()).filter(Boolean),
      });
      toast.success(`Preset "${name}" saved.`);
      setPresetName(""); setShowSavePreset(false); loadPresets();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save preset.");
    }
  };

  const saveSchedule = async (id, schedule) => {
    try {
      await api.patch(`/admin/outreach/presets/${id}`, { schedule });
      toast.success(schedule.enabled ? "Schedule set." : "Schedule turned off.");
      setScheduleEditor(null); loadPresets();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save schedule.");
    }
  };

  const deletePreset = async (id) => {
    try { await api.delete(`/admin/outreach/presets/${id}`); loadPresets(); toast.message("Preset removed."); }
    catch { toast.error("Could not remove preset."); }
  };

  const progressText = (p) => {
    if (!p) return "Working…";
    if (p.phase === "finding") return "Searching Google Maps for real local businesses…";
    if (p.phase === "analyzing") return `Found ${p.found ?? 0} · analyzing ${p.analyzed ?? 0}/${p.to_analyze ?? 0}`;
    if (p.phase === "drafting") return `Found ${p.found ?? 0} · analyzed ${p.analyzed ?? 0} · drafting ${p.drafted ?? 0}/${p.to_draft ?? 0}`;
    if (p.phase === "sending") return `Found ${p.found ?? 0} · analyzed ${p.analyzed ?? 0} · drafted ${p.drafted ?? 0} · sending ${p.sent ?? 0}`;
    if (p.phase === "done") return `Found ${p.found ?? 0} · analyzed ${p.analyzed ?? 0} · drafted ${p.drafted ?? 0} — finishing up…`;
    return "Working…";
  };
  const isBusy = running || finding || syncing;

  const patchLead = async (id, patch, quiet) => {
    try {
      const { data } = await api.patch(`/admin/outreach/leads/${id}`, patch);
      setLeads((ls) => ls.map((l) => (l.id === id ? data.lead : l)));
      if (!quiet) toast.success("Saved.");
      loadDash();
      return data.lead;
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save."); }
  };

  const saveSched = async (patch) => {
    try {
      const { data } = await api.put("/admin/outreach/mass-send-schedule", patch);
      setConfig((c) => ({ ...c, mass_send_schedule: data.mass_send_schedule }));
      setSchedNext(data.next_local);
      toast.success(data.mass_send_schedule.enabled ? `Scheduled · next ${data.next_local}` : "Auto-send schedule turned off.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save schedule."); }
  };

  const massSend = async (channel) => {
    const eligible = channel === "email"
      ? leads.filter((l) => ["queued", "drafted"].includes(l.status)).length
      : leads.filter((l) => l.phone && l.sms_status !== "sent" && !["dismissed", "converted", "opted_out"].includes(l.status)).length;
    if (!eligible) { toast.message(channel === "email" ? "No leads are ready to email yet." : "No leads with a phone number to text yet."); return; }
    if (channel === "sms" && !smsStatus.configured) { toast.error("Connect Twilio first to send text messages."); return; }
    if (!window.confirm(`Send ${channel === "email" ? "emails" : "text messages"} to ${eligible} lead${eligible > 1 ? "s" : ""} now?`)) return;
    setMassSending(channel);
    try {
      const { data } = await api.post("/admin/outreach/send-all", { channel });
      toast.success(`Sent ${data.sent} · ${data.failed} failed · ${data.skipped} skipped`);
      if (data.errors?.length) toast.message(data.errors[0]);
      loadDash(); loadLeads();
    } catch (e) { toast.error(e?.response?.data?.detail || "Mass send failed."); }
    finally { setMassSending(null); }
  };

  const toggleRating = (r) =>
    setFind((f) => ({ ...f, ratings: f.ratings.includes(r) ? f.ratings.filter((x) => x !== r) : [...f.ratings, r] }));

  if (!config) return <div className="flex items-center gap-2 text-sm text-[#1e3d3a]/55 py-8"><Loader2 className="h-4 w-4 animate-spin" /> Loading engine…</div>;

  return (
    <div data-testid="outreach-engine" className="space-y-5">
      {costPreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#1e3d3a]/40 backdrop-blur-sm p-4" data-testid="outreach-cost-preview"
          onClick={() => setCostPreview(null)}>
          <div className="w-full max-w-md rounded-2xl bg-white border border-[#1e3d3a]/15 shadow-2xl p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2">
              <Zap className="h-5 w-5 text-[#a3823f]" />
              <h3 className="text-base font-semibold text-[#1e3d3a]">Estimated cost for this run</h3>
            </div>
            <p className="text-[11px] text-[#1e3d3a]/55 -mt-2">
              {costPreview.est.places.toLocaleString()} businesses ({nLeads()}/city × {costPreview.est.cities} {costPreview.est.cities === 1 ? "city" : "cities"}) before spending anything.
            </p>
            <div className="rounded-xl border border-[#1e3d3a]/12 divide-y divide-[#1e3d3a]/8 text-sm">
              <div className="flex items-center justify-between px-4 py-2.5" data-testid="cost-apify-row">
                <span className="text-[#1e3d3a]/70">Apify (Google Maps scrape)</span>
                <span className="font-semibold text-[#1e3d3a]">${costPreview.est.apifyLow.toFixed(2)}–${costPreview.est.apifyHigh.toFixed(2)}</span>
              </div>
              <div className="flex items-center justify-between px-4 py-2.5" data-testid="cost-ai-row">
                <span className="text-[#1e3d3a]/70">AI {costPreview.est.aiCalls > 0 ? `(${costPreview.est.aiCalls.toLocaleString()} calls · analyze + draft)` : "(not used)"}</span>
                <span className="font-semibold text-[#1e3d3a]">{costPreview.est.aiCalls > 0 ? `$${costPreview.est.aiLow.toFixed(2)}–$${costPreview.est.aiHigh.toFixed(2)}` : "$0.00"}</span>
              </div>
              <div className="flex items-center justify-between px-4 py-3 bg-[#c9a961]/8">
                <span className="text-[10px] uppercase tracking-[0.16em] text-[#a3823f]">Estimated total</span>
                <span className="font-bold text-[#1e3d3a]" data-testid="cost-total">${costPreview.est.totalLow.toFixed(2)}–${costPreview.est.totalHigh.toFixed(2)}</span>
              </div>
            </div>
            <p className="text-[10px] text-[#1e3d3a]/45 leading-relaxed">
              Estimate only — actual Apify cost depends on your plan, and AI cost applies only to <span className="font-medium">new</span> leads (businesses already in your pipeline are skipped, lowering the AI total).
            </p>
            <div className="flex items-center justify-end gap-2 pt-1">
              <button type="button" data-testid="cost-cancel" onClick={() => setCostPreview(null)}
                className="rounded-full border border-[#1e3d3a]/20 text-[#1e3d3a]/70 px-4 py-2 text-[11px] uppercase tracking-[0.16em] hover:border-[#b23a48]/40 hover:text-[#b23a48] transition-colors duration-300">
                Cancel
              </button>
              <button type="button" data-testid="cost-confirm" onClick={confirmCostPreview}
                className="inline-flex items-center gap-1.5 rounded-full bg-[#1e3d3a] text-white px-4 py-2 text-[11px] uppercase tracking-[0.16em] hover:bg-[#c9a961] hover:text-[#1e3d3a] transition-colors duration-300">
                <PlayCircle className="h-3.5 w-3.5" /> Confirm &amp; run
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[#1e3d3a]/12 bg-white px-5 py-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#1e3d3a]/55">
            <Radar className="h-3.5 w-3.5 text-[#c9a961]" /> Outreach Engine
          </div>
          <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[10px] uppercase tracking-[0.16em] ${config.mode === "autonomous" ? "border-[#6b7c3a]/40 bg-[#6b7c3a]/10 text-[#4d5a24]" : "border-[#c9a961]/40 bg-[#c9a961]/10 text-[#a3823f]"}`} data-testid="outreach-mode-chip">
            {config.mode === "autonomous" ? "Autonomous" : "Draft · approve"}
          </span>
          {config.enabled ? (
            <span className="inline-flex items-center gap-1 rounded-full border border-[#6b7c3a]/40 bg-[#6b7c3a]/10 text-[#4d5a24] px-2.5 py-0.5 text-[10px] uppercase tracking-[0.16em]" data-testid="outreach-enabled-chip">Auto-run on</span>
          ) : (
            <span className="inline-flex items-center gap-1 rounded-full border border-[#1e3d3a]/12 bg-[#1e3d3a]/5 text-[#1e3d3a]/50 px-2.5 py-0.5 text-[10px] uppercase tracking-[0.16em]" data-testid="outreach-disabled-chip">Auto-run off</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button type="button" data-testid="outreach-inbox-toggle" onClick={openInbox}
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[10px] uppercase tracking-[0.16em] transition-colors duration-300 ${showInbox ? "border-[#c9a961] bg-[#c9a961]/12 text-[#a3823f]" : "border-[#1e3d3a]/15 text-[#1e3d3a]/70 hover:border-[#c9a961] hover:text-[#a3823f]"}`}>
            <Inbox className="h-3.5 w-3.5" /> Inbox
            {inbox.conversations.filter((c) => c.unread).length > 0 && (
              <span className="ml-0.5 rounded-full bg-[#b23a48] text-white text-[9px] px-1.5 py-0.5" data-testid="outreach-inbox-unread">
                {inbox.conversations.filter((c) => c.unread).length}
              </span>
            )}
          </button>
          <button type="button" data-testid="outreach-analytics-toggle" onClick={openAnalytics}
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[10px] uppercase tracking-[0.16em] transition-colors duration-300 ${showAnalytics ? "border-[#c9a961] bg-[#c9a961]/12 text-[#a3823f]" : "border-[#1e3d3a]/15 text-[#1e3d3a]/70 hover:border-[#c9a961] hover:text-[#a3823f]"}`}>
            <BarChart3 className="h-3.5 w-3.5" /> Analytics
          </button>
          <button type="button" data-testid="outreach-config-toggle" onClick={() => setShowConfig((s) => !s)}
            className="inline-flex items-center gap-1.5 rounded-full border border-[#1e3d3a]/15 text-[#1e3d3a]/70 px-3 py-1.5 text-[10px] uppercase tracking-[0.16em] hover:border-[#c9a961] hover:text-[#a3823f] transition-colors duration-300">
            <Settings2 className="h-3.5 w-3.5" /> Settings
          </button>
          <button type="button" data-testid="outreach-sync-replies" onClick={syncReplies} disabled={syncing || !gmail.connected}
            className="inline-flex items-center gap-1.5 rounded-full border border-[#1e3d3a]/15 text-[#1e3d3a]/70 px-3 py-1.5 text-[10px] uppercase tracking-[0.16em] hover:border-[#c9a961] hover:text-[#a3823f] disabled:opacity-40 transition-colors duration-300">
            {syncing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Sync replies
          </button>
          <div className="flex items-center gap-1.5 rounded-full border border-[#1e3d3a]/15 bg-white pl-3 pr-1 py-1" title="How many businesses to find per city this run (max 1000). Larger runs use more Apify + AI credits.">
            <label htmlFor="outreach-run-limit" className="text-[10px] uppercase tracking-[0.16em] text-[#1e3d3a]/50 whitespace-nowrap">Leads</label>
            <input id="outreach-run-limit" data-testid="outreach-run-limit" type="number" min={1} max={1000}
              value={runLimit}
              onChange={(e) => setRunLimit(e.target.value === "" ? "" : Math.max(1, Math.min(1000, parseInt(e.target.value, 10) || 1)))}
              onBlur={(e) => { if (e.target.value === "") setRunLimit(25); }}
              className="w-16 rounded-full bg-transparent px-1 py-0.5 text-xs text-[#1e3d3a] font-semibold focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" />
          </div>
          <button type="button" data-testid="outreach-run" onClick={runEngine} disabled={running}
            className="inline-flex items-center gap-2 rounded-full bg-[#1e3d3a] text-white px-4 py-1.5 text-xs uppercase tracking-[0.2em] hover:bg-[#c9a961] hover:text-[#1e3d3a] transition-colors duration-300 disabled:opacity-60">
            {running ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Running…</> : <><PlayCircle className="h-3.5 w-3.5" /> Run engine</>}
          </button>
          {isBusy && (
            <button type="button" data-testid="outreach-stop" onClick={stopEngine} disabled={stopping}
              className="inline-flex items-center gap-1.5 rounded-full border border-[#b23a48]/50 bg-[#b23a48]/8 text-[#b23a48] px-4 py-1.5 text-xs uppercase tracking-[0.2em] hover:bg-[#b23a48] hover:text-white transition-colors duration-300 disabled:opacity-50">
              {stopping ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Square className="h-3.5 w-3.5" />} Stop
            </button>
          )}
          {!isBusy && (
            <button type="button" data-testid="outreach-enrich-missing" onClick={enrichMissing}
              title="Re-scrape websites for leads missing an email and find decision-maker contacts"
              className="inline-flex items-center gap-1.5 rounded-full border border-[#1e3d3a]/20 text-[#1e3d3a]/70 px-4 py-1.5 text-xs uppercase tracking-[0.2em] hover:border-[#c9a961] hover:text-[#a3823f] transition-colors duration-300">
              <FileSearch className="h-3.5 w-3.5" /> Enrich missing emails
            </button>
          )}
        </div>
      </div>

      {lastRunCost && (
        <p data-testid="outreach-last-run-cost" className="text-[11px] text-[#4d5a24] flex items-center gap-1.5 -mt-2 px-1">
          <Zap className="h-3.5 w-3.5 text-[#6b7c3a]" />
          Last {lastRunCost.kind === "find" ? "search" : "run"} actually cost <span className="font-semibold">${(lastRunCost.cost.total_low ?? 0).toFixed(2)}–${(lastRunCost.cost.total_high ?? 0).toFixed(2)}</span>
          <span className="text-[#1e3d3a]/45">({lastRunCost.cost.places} businesses scraped{lastRunCost.cost.ai_calls ? `, ${lastRunCost.cost.ai_calls} AI calls` : ""})</span>
        </p>
      )}

      {Number(runLimit) >= 200 && (
        <p data-testid="outreach-run-cost-hint" className="text-[11px] text-[#a3823f] flex items-center gap-1.5 -mt-2 px-1">
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#c9a961]" />
          Heads up: a {runLimit}-lead run per city scrapes that many businesses on Apify and runs the AI on each — larger runs use noticeably more credits.
        </p>
      )}

      {/* Live job progress */}
      {showInbox && (
        <InboxPanel data={inbox} activeId={activeId} onSelect={setActiveId}
          replyText={replyText} setReplyText={setReplyText} onSend={sendReply} />
      )}
      {showAnalytics && <AnalyticsPanel data={analytics} onSetGoal={setGoal} />}

      {isBusy && (
        <div data-testid="outreach-progress" className="flex items-center gap-3 rounded-2xl border border-[#6b7c3a]/30 bg-[#6b7c3a]/6 px-5 py-3">
          <Loader2 className="h-4 w-4 animate-spin text-[#6b7c3a] shrink-0" />
          <span className="text-sm text-[#4d5a24] font-medium" data-testid="outreach-progress-text">
            {finding ? (jobProgress?.phase === "done" ? `Found ${jobProgress?.found ?? 0} businesses.` : "Searching Google Maps for real local businesses…") : progressText(jobProgress)}
          </span>
          <span className="text-[11px] text-[#6b7c3a]/70">running in the background — you can keep working</span>
        </div>
      )}

      {/* Gmail connect banner */}
      {!gmail.connected && (
        <div data-testid="outreach-gmail-banner" className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[#c9a961]/40 bg-[#c9a961]/8 px-5 py-3">
          <div className="flex items-start gap-2 text-sm text-[#a3823f]">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>Connect Google Workspace with Gmail access so the engine can send from your inbox and auto-detect replies. You'll authorize once (Calendar + Gmail).</span>
          </div>
          <button type="button" data-testid="outreach-connect-gmail" onClick={connectGoogle}
            className="inline-flex items-center gap-1.5 rounded-full bg-[#1e3d3a] text-white px-4 py-1.5 text-[10px] uppercase tracking-[0.16em] font-semibold hover:bg-[#c9a961] hover:text-[#1e3d3a] transition-colors duration-300 shrink-0">
            <Link2 className="h-3 w-3" /> Connect Google
          </button>
        </div>
      )}
      {gmail.connected && (
        <div className="flex items-center gap-2 text-[11px] text-[#4d5a24]" data-testid="outreach-gmail-connected">
          <CheckCircle2 className="h-3.5 w-3.5" /> Sending from <span className="font-semibold">{gmail.email}</span>
        </div>
      )}

      {/* Funnel dashboard */}
      <div data-testid="outreach-funnel" className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
        {FUNNEL.map(({ key, label, icon: Icon, filter }) => {
          const active = statusFilter === filter;
          return (
            <button
              key={key}
              type="button"
              data-testid={`funnel-${key}`}
              onClick={() => focusLeads(filter)}
              title={`Show ${label.toLowerCase()} leads`}
              className={`text-left rounded-xl border px-3 py-3 transition-all duration-300 cursor-pointer hover:-translate-y-0.5 hover:border-[#c9a961]/60 hover:shadow-sm ${active ? "border-[#1e3d3a] bg-[#1e3d3a]/[0.04] ring-1 ring-[#c9a961]/40" : "border-[#1e3d3a]/12 bg-white"}`}>
              <div className="flex items-center gap-1.5 text-[9px] uppercase tracking-[0.18em] text-[#1e3d3a]/50">
                <Icon className="h-3 w-3 text-[#c9a961]" /> {label}
              </div>
              <div className="mt-1 font-head text-2xl text-[#1e3d3a]">{funnel[key] ?? 0}</div>
            </button>
          );
        })}
      </div>

      {/* Config panel */}
      {showConfig && (
        <div data-testid="outreach-config-panel" className="rounded-2xl border border-[#1e3d3a]/12 bg-white p-5 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-[#1e3d3a]/55">Mode</label>
              <div className="mt-1.5 flex gap-2">
                {["draft_approve", "autonomous"].map((m) => (
                  <button key={m} type="button" data-testid={`outreach-mode-${m}`}
                    onClick={() => saveConfig({ mode: m })}
                    className={`flex-1 rounded-xl border px-3 py-2 text-xs transition-colors duration-300 ${config.mode === m ? "border-[#c9a961] bg-[#c9a961]/12 text-[#a3823f]" : "border-[#1e3d3a]/15 text-[#1e3d3a]/60 hover:border-[#c9a961]/50"}`}>
                    {m === "draft_approve" ? "Draft → I approve" : "Fully autonomous"}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-[#1e3d3a]/55">Daily send cap</label>
              <div className="mt-1.5 flex items-center gap-2">
                <input type="number" data-testid="outreach-cap-input" defaultValue={config.daily_cap}
                  onBlur={(e) => saveConfig({ daily_cap: Number(e.target.value) })}
                  className="w-28 rounded-xl border border-[#1e3d3a]/15 bg-white px-3 py-2 text-sm focus:border-[#c9a961] focus:outline-none" />
                <label className="flex items-center gap-2 text-xs text-[#1e3d3a]/60">
                  <input type="checkbox" data-testid="outreach-enabled-toggle" checked={!!config.enabled}
                    onChange={(e) => saveConfig({ enabled: e.target.checked })} />
                  Auto-run daily
                </label>
              </div>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-[#1e3d3a]/55">Follow-up cadence (days)</label>
              <input type="text" data-testid="outreach-followup-input" defaultValue={(config.followup_days || []).join(", ")}
                onBlur={(e) => saveConfig({ followup_days: e.target.value.split(",").map((x) => parseInt(x.trim(), 10)).filter((n) => !isNaN(n)) })}
                className="mt-1.5 w-full rounded-xl border border-[#1e3d3a]/15 bg-white px-3 py-2 text-sm focus:border-[#c9a961] focus:outline-none" placeholder="3, 7, 14" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-[#1e3d3a]/55">Default ICP location</label>
              <input type="text" data-testid="outreach-icp-location" defaultValue={config.icp_location}
                onBlur={(e) => saveConfig({ icp_location: e.target.value })}
                className="mt-1.5 w-full rounded-xl border border-[#1e3d3a]/15 bg-white px-3 py-2 text-sm focus:border-[#c9a961] focus:outline-none" placeholder="e.g. Dallas, TX" />
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-[#1e3d3a]/55">Default lead source</label>
            <div className="mt-1.5 flex gap-2">
              {[{ k: "both", label: "BBB + Internet" }, { k: "web", label: "Internet search" }, { k: "bbb", label: "BBB feed" }].map(({ k, label }) => (
                <button key={k} type="button" data-testid={`outreach-cfg-source-${k}`} onClick={() => saveConfig({ lead_source: k })}
                  className={`flex-1 rounded-xl border px-3 py-2 text-xs transition-colors duration-300 ${config.lead_source === k ? "border-[#c9a961] bg-[#c9a961]/12 text-[#a3823f]" : "border-[#1e3d3a]/15 text-[#1e3d3a]/60 hover:border-[#c9a961]/50"}`}>
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-[#1e3d3a]/55">Email signature</label>
            <textarea data-testid="outreach-signature" defaultValue={config.signature} rows={3}
              onBlur={(e) => saveConfig({ signature: e.target.value })}
              className="mt-1.5 w-full rounded-xl border border-[#1e3d3a]/15 bg-white px-3 py-2 text-sm focus:border-[#c9a961] focus:outline-none" />
          </div>

          {/* First-email template */}
          <div className="rounded-xl border border-[#c9a961]/30 bg-[#c9a961]/5 p-4 space-y-3" data-testid="outreach-template-section">
            {currentUser?.username && (
              <div className="flex items-center gap-2 text-[11px] text-[#a3823f]" data-testid="outreach-my-templates-note">
                <span className="h-2 w-2 rounded-full" style={{ background: founderColor(currentUser.username) }} />
                Your personal templates, {founderName(currentUser.username)} — only you send with these.
              </div>
            )}
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="text-[10px] uppercase tracking-[0.24em] text-[#a3823f]">First-email template</div>
              <div className="flex gap-1.5">
                {[{ k: "ai", label: "AI writes it" }, { k: "hybrid", label: "Use as backbone" }, { k: "template", label: "Send verbatim" }].map(({ k, label }) => (
                  <button key={k} type="button" data-testid={`outreach-template-mode-${k}`} onClick={() => saveConfig({ template_mode: k })}
                    className={`rounded-full border px-2.5 py-1 text-[10px] uppercase tracking-[0.12em] transition-colors duration-300 ${config.template_mode === k ? "border-[#1e3d3a] bg-[#1e3d3a] text-white" : "border-[#1e3d3a]/15 text-[#1e3d3a]/55 hover:border-[#c9a961]"}`}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <p className="text-[11px] text-[#1e3d3a]/55">
              {config.template_mode === "ai"
                ? "AI writes each first email from scratch, personalized per prospect. Your template below is ignored — use it as a fallback or switch modes."
                : config.template_mode === "template"
                  ? "Your template is sent exactly as written, with placeholders filled in. No AI rewriting."
                  : "AI keeps your template's structure, tone and call-to-action, fills placeholders, and personalizes the opening line per prospect."}
            </p>
            <input data-testid="outreach-template-subject" defaultValue={config.template_subject}
              onBlur={(e) => saveConfig({ template_subject: e.target.value })}
              placeholder="Subject template, e.g. {{first_name}}, a quick idea for {{business_name}}"
              className="w-full rounded-lg border border-[#1e3d3a]/15 bg-white px-3 py-2 text-sm font-medium focus:border-[#c9a961] focus:outline-none" />
            <textarea data-testid="outreach-template-body" defaultValue={config.template_body} rows={8}
              onBlur={(e) => saveConfig({ template_body: e.target.value })}
              placeholder={"Body template. Example:\n\nHi {{first_name}},\n\nI came across {{business_name}} in {{city}} and wanted to reach out. Companies in {{industry}} often struggle with {{pain_point}}.\n\nAt Elevare we help you regain control, cut costs and raise your reputation.\n\nOpen to a quick 15-minute call?"}
              className="w-full rounded-lg border border-[#1e3d3a]/15 bg-white px-3 py-2 text-sm leading-relaxed focus:border-[#c9a961] focus:outline-none whitespace-pre-wrap" />
            <div className="text-[10px] text-[#1e3d3a]/50">
              Placeholders:{" "}
              {["{{first_name}}", "{{owner_name}}", "{{business_name}}", "{{city}}", "{{location}}", "{{industry}}", "{{bbb_rating}}", "{{pain_point}}", "{{pain_points}}", "{{elevare_angle}}"].map((p) => (
                <code key={p} className="mr-1 rounded bg-[#1e3d3a]/6 px-1 py-0.5 text-[#a3823f]">{p}</code>
              ))}
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-[#1e3d3a]/55">Extra AI instructions (optional)</label>
              <input data-testid="outreach-instructions" defaultValue={config.outreach_instructions}
                onBlur={(e) => saveConfig({ outreach_instructions: e.target.value })}
                placeholder="e.g. Keep it under 120 words, mention our BBB-score guarantee, friendly tone"
                className="mt-1.5 w-full rounded-lg border border-[#1e3d3a]/15 bg-white px-3 py-2 text-sm focus:border-[#c9a961] focus:outline-none" />
            </div>
            {/* A/B test toggle + variant B */}
            <div className="rounded-lg border border-[#a3823f]/25 bg-white p-3 space-y-2" data-testid="outreach-ab-section">
              <label className="flex items-center gap-2 text-xs text-[#1e3d3a]/75">
                <input type="checkbox" data-testid="outreach-ab-enabled" checked={!!config.ab_enabled}
                  onChange={(e) => saveConfig({ ab_enabled: e.target.checked })} />
                <span className="font-medium">A/B test messages</span>
                <span className="text-[10px] text-[#1e3d3a]/45">— rotate two versions 50/50; the winner is crowned in Analytics</span>
              </label>
              {config.ab_enabled && (
                <div className="space-y-2 pt-1">
                  <div className="text-[10px] uppercase tracking-[0.16em] text-[#a3823f]">Variant B (email)</div>
                  <input data-testid="outreach-template-subject-b" defaultValue={config.template_subject_b}
                    onBlur={(e) => saveConfig({ template_subject_b: e.target.value })}
                    placeholder="Variant B subject (optional — falls back to A)"
                    className="w-full rounded-lg border border-[#1e3d3a]/15 bg-white px-3 py-2 text-sm focus:border-[#c9a961] focus:outline-none" />
                  <textarea data-testid="outreach-template-body-b" defaultValue={config.template_body_b} rows={5}
                    onBlur={(e) => saveConfig({ template_body_b: e.target.value })}
                    placeholder="Variant B body — a different angle to test against A"
                    className="w-full rounded-lg border border-[#1e3d3a]/15 bg-white px-3 py-2 text-sm leading-relaxed focus:border-[#c9a961] focus:outline-none whitespace-pre-wrap" />
                </div>
              )}
            </div>
          </div>

          {/* Team & lead assignment */}
          {founders.length > 0 && (
            <div className="rounded-xl border border-[#6b7c3a]/30 bg-[#6b7c3a]/[0.05] p-4 space-y-3" data-testid="outreach-team-section">
              <div className="flex items-center gap-2">
                <Trophy className="h-4 w-4 text-[#a3823f]" />
                <span className="text-[11px] uppercase tracking-[0.2em] text-[#1e3d3a]/70">Team &amp; lead assignment</span>
              </div>
              <label className="flex items-center gap-2 text-xs text-[#1e3d3a]/75">
                <input type="checkbox" data-testid="outreach-auto-assign" checked={!!config.auto_assign}
                  onChange={(e) => saveConfig({ auto_assign: e.target.checked })} />
                Auto-assign brand-new leads to a founder
              </label>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[10px] uppercase tracking-[0.16em] text-[#1e3d3a]/50">Method</span>
                {[{ k: "balanced", label: "Lighter pipeline" }, { k: "round_robin", label: "Round-robin" }].map(({ k, label }) => (
                  <button key={k} type="button" data-testid={`outreach-assign-${k}`} onClick={() => saveConfig({ assignment_mode: k })}
                    className={`rounded-full border px-2.5 py-1 text-[10px] uppercase tracking-[0.12em] transition-colors duration-300 ${config.assignment_mode === k ? "border-[#1e3d3a] bg-[#1e3d3a] text-white" : "border-[#1e3d3a]/15 text-[#1e3d3a]/55 hover:border-[#c9a961]"}`}>
                    {label}
                  </button>
                ))}
              </div>
              <label className="flex items-center gap-2 text-xs text-[#1e3d3a]/75 pt-1 border-t border-[#1e3d3a]/8">
                <input type="checkbox" data-testid="outreach-founder-digest" checked={!!config.founder_digest}
                  onChange={(e) => saveConfig({ founder_digest: e.target.checked })} />
                Email each founder a morning digest of their overnight replies
              </label>
              <div className="flex items-center gap-2 text-[11px] text-[#1e3d3a]/60">
                <span>Send at</span>
                <select data-testid="outreach-digest-hour" value={config.digest_hour ?? 8}
                  onChange={(e) => saveConfig({ digest_hour: Number(e.target.value) })}
                  className="rounded border border-[#1e3d3a]/15 bg-white px-2 py-1 focus:border-[#c9a961] focus:outline-none">
                  {Array.from({ length: 24 }, (_, i) => <option key={i} value={i}>{String(i).padStart(2, "0")}:00</option>)}
                </select>
                <span className="text-[10px] text-[#1e3d3a]/40">{(config.quiet_hours?.tz || "America/New_York").replace("_", " ")}</span>
              </div>
            </div>
          )}

          {/* SMS / text-message settings */}
          <div className="rounded-xl border border-[#1e3d3a]/15 bg-[#1e3d3a]/[0.03] p-4 space-y-3" data-testid="outreach-sms-section">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <MessageSquare className="h-4 w-4 text-[#a3823f]" />
                <span className="text-[11px] uppercase tracking-[0.2em] text-[#1e3d3a]/70">Text message (SMS)</span>
              </div>
              {smsStatus.configured ? (
                <span className="inline-flex items-center gap-1 rounded-full border border-[#6b7c3a]/40 bg-[#6b7c3a]/10 text-[#4d5a24] px-2.5 py-0.5 text-[10px] uppercase tracking-[0.14em]" data-testid="outreach-sms-connected">
                  <CheckCircle2 className="h-3 w-3" /> Twilio {smsStatus.from_number}
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 rounded-full border border-[#c9a961]/50 bg-[#c9a961]/10 text-[#a3823f] px-2.5 py-0.5 text-[10px] uppercase tracking-[0.14em]" data-testid="outreach-sms-disconnected">
                  <AlertTriangle className="h-3 w-3" /> Needs Twilio
                </span>
              )}
            </div>
            {!smsStatus.configured && (
              <p className="text-[11px] text-[#1e3d3a]/55" data-testid="outreach-sms-connect-note">
                To send texts, add <code className="rounded bg-[#1e3d3a]/6 px-1 text-[#a3823f]">TWILIO_ACCOUNT_SID</code>,
                {" "}<code className="rounded bg-[#1e3d3a]/6 px-1 text-[#a3823f]">TWILIO_AUTH_TOKEN</code> and a
                {" "}<code className="rounded bg-[#1e3d3a]/6 px-1 text-[#a3823f]">TWILIO_FROM_NUMBER</code> to the backend. You can compose templates now.
              </p>
            )}
            <div className="flex items-center gap-2 flex-wrap">
              <label className="text-[10px] uppercase tracking-[0.16em] text-[#1e3d3a]/50">Message style</label>
              {["template", "ai", "hybrid"].map((k) => (
                <button key={k} type="button" data-testid={`outreach-sms-mode-${k}`} onClick={() => saveConfig({ sms_mode: k })}
                  className={`rounded-full border px-2.5 py-1 text-[10px] uppercase tracking-[0.12em] transition-colors duration-300 ${config.sms_mode === k ? "border-[#1e3d3a] bg-[#1e3d3a] text-white" : "border-[#1e3d3a]/15 text-[#1e3d3a]/55 hover:border-[#c9a961]"}`}>
                  {k === "template" ? "Verbatim" : k === "ai" ? "AI writes" : "Hybrid"}
                </button>
              ))}
            </div>
            <textarea data-testid="outreach-sms-template" defaultValue={config.sms_template} rows={3}
              onBlur={(e) => saveConfig({ sms_template: e.target.value })}
              placeholder="Hi {{first_name}}, this is Kalib with Elevare Solutions…"
              className="w-full rounded-lg border border-[#1e3d3a]/15 bg-white px-3 py-2 text-sm leading-relaxed focus:border-[#c9a961] focus:outline-none whitespace-pre-wrap" />
            <p className="text-[10px] text-[#1e3d3a]/45">Same placeholders as email · keep it short (1–2 sentences).</p>
            {config.ab_enabled && (
              <textarea data-testid="outreach-sms-template-b" defaultValue={config.sms_template_b} rows={3}
                onBlur={(e) => saveConfig({ sms_template_b: e.target.value })}
                placeholder="Variant B text (A/B test) — a different angle to test against A"
                className="w-full rounded-lg border border-[#a3823f]/25 bg-white px-3 py-2 text-sm leading-relaxed focus:border-[#c9a961] focus:outline-none whitespace-pre-wrap" />
            )}
            <div className="flex items-center gap-4 flex-wrap">
              <label className="flex items-center gap-2 text-xs text-[#1e3d3a]/70">
                <input type="checkbox" data-testid="outreach-sms-optout" checked={!!config.sms_optout}
                  onChange={(e) => saveConfig({ sms_optout: e.target.checked })} />
                Append “Reply STOP to opt out”
              </label>
              <label className="flex items-center gap-2 text-xs text-[#1e3d3a]/70">
                <input type="checkbox" data-testid="outreach-sms-auto" checked={!!config.sms_auto}
                  onChange={(e) => saveConfig({ sms_auto: e.target.checked })} />
                Auto-text new leads in the pipeline
              </label>
            </div>
            {/* Quiet hours — holds AUTOMATED blasts to business hours */}
            <div className="rounded-lg border border-[#1e3d3a]/12 bg-white p-3 space-y-2" data-testid="outreach-quiet-hours">
              <label className="flex items-center gap-2 text-xs text-[#1e3d3a]/70">
                <input type="checkbox" data-testid="outreach-quiet-enabled"
                  checked={!!config.quiet_hours?.enabled}
                  onChange={(e) => saveConfig({ quiet_hours: { ...(config.quiet_hours || {}), enabled: e.target.checked } })} />
                Quiet hours — hold scheduled &amp; auto sends to business hours
              </label>
              <div className="flex items-center gap-2 flex-wrap text-[11px] text-[#1e3d3a]/60">
                <span>From</span>
                <select data-testid="outreach-quiet-start" value={config.quiet_hours?.start ?? 8}
                  onChange={(e) => saveConfig({ quiet_hours: { ...(config.quiet_hours || {}), start: Number(e.target.value) } })}
                  className="rounded border border-[#1e3d3a]/15 bg-white px-2 py-1 focus:border-[#c9a961] focus:outline-none">
                  {Array.from({ length: 24 }, (_, i) => <option key={i} value={i}>{String(i).padStart(2, "0")}:00</option>)}
                </select>
                <span>to</span>
                <select data-testid="outreach-quiet-end" value={config.quiet_hours?.end ?? 20}
                  onChange={(e) => saveConfig({ quiet_hours: { ...(config.quiet_hours || {}), end: Number(e.target.value) } })}
                  className="rounded border border-[#1e3d3a]/15 bg-white px-2 py-1 focus:border-[#c9a961] focus:outline-none">
                  {Array.from({ length: 24 }, (_, i) => <option key={i + 1} value={i + 1}>{String(i + 1).padStart(2, "0")}:00</option>)}
                </select>
                <select data-testid="outreach-quiet-tz" value={config.quiet_hours?.tz || "America/New_York"}
                  onChange={(e) => saveConfig({ quiet_hours: { ...(config.quiet_hours || {}), tz: e.target.value } })}
                  className="rounded border border-[#1e3d3a]/15 bg-white px-2 py-1 focus:border-[#c9a961] focus:outline-none">
                  {TZS.map((z) => <option key={z} value={z}>{z.replace("_", " ")}</option>)}
                </select>
              </div>
              <p className="text-[10px] text-[#1e3d3a]/40">Manual "Mass send now" always sends immediately — this only holds scheduled blasts &amp; auto-texts.</p>
              <label className="flex items-center gap-2 text-xs text-[#1e3d3a]/70 pt-1 border-t border-[#1e3d3a]/8">
                <input type="checkbox" data-testid="outreach-quiet-perlead"
                  checked={!!config.quiet_hours?.per_lead_tz}
                  onChange={(e) => saveConfig({ quiet_hours: { ...(config.quiet_hours || {}), per_lead_tz: e.target.checked } })} />
                Use each lead's <b>own local timezone</b> for SMS (so a Texas lead and a California lead each get texted in their own business hours)
              </label>
            </div>
          </div>
        </div>
      )}
      <div data-testid="outreach-finder" className="rounded-2xl border border-[#1e3d3a]/12 bg-white p-5">
        <button type="button" data-testid="outreach-collapse-finder" onClick={() => toggleCollapse("finder")}
          className="flex w-full items-center justify-between gap-2 text-[10px] uppercase tracking-[0.24em] text-[#c9a961] mb-3 hover:text-[#a3823f] transition-colors duration-300">
          <span>Lead finder · ideal client profile</span>
          {collapsed.finder ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>
        {!collapsed.finder && (<>

        {/* Saved presets */}
        <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="outreach-presets">
          <span className="text-[10px] uppercase tracking-[0.16em] text-[#1e3d3a]/50">Presets</span>
          {presets.length === 0 && <span className="text-[11px] text-[#1e3d3a]/40">None saved yet — set up a search below and save it.</span>}
          {presets.map((p) => (
            <span key={p.id} data-testid={`outreach-preset-${p.id}`}
              className="inline-flex items-center gap-1 rounded-full border border-[#1e3d3a]/15 bg-[#eae7de]/40 pl-1 pr-1 py-0.5">
              <button type="button" title="Load this recipe into the finder" onClick={() => applyPreset(p)}
                data-testid={`outreach-preset-apply-${p.id}`}
                className="px-2 py-0.5 text-[11px] text-[#1e3d3a] hover:text-[#a3823f] transition-colors duration-300">
                {p.name}
                {p.locations && p.locations.length > 1 && <span className="ml-1 text-[#a3823f]">·{p.locations.length} cities</span>}
                {p.schedule?.enabled && <Clock className="inline h-3 w-3 ml-1 text-[#6b7c3a]" />}
              </button>
              <button type="button" title="Schedule this preset" onClick={() => setScheduleEditor(scheduleEditor === p.id ? null : p.id)}
                data-testid={`outreach-preset-schedule-${p.id}`}
                className={`grid place-items-center h-5 w-5 rounded-full transition-colors duration-300 ${p.schedule?.enabled ? "bg-[#6b7c3a] text-white" : "text-[#1e3d3a]/40 hover:text-[#4d5a24]"}`}>
                <Clock className="h-3 w-3" />
              </button>
              <button type="button" title="Fire this preset now" onClick={() => firePreset(p)} disabled={isBusy}
                data-testid={`outreach-preset-fire-${p.id}`}
                className="grid place-items-center h-5 w-5 rounded-full bg-[#1e3d3a] text-white hover:bg-[#c9a961] hover:text-[#1e3d3a] disabled:opacity-40 transition-colors duration-300">
                <PlayCircle className="h-3 w-3" />
              </button>
              <button type="button" title="Delete preset" onClick={() => deletePreset(p.id)}
                data-testid={`outreach-preset-delete-${p.id}`}
                className="grid place-items-center h-5 w-5 rounded-full text-[#1e3d3a]/40 hover:text-[#b23a48] transition-colors duration-300">
                <Ban className="h-3 w-3" />
              </button>
            </span>
          ))}
          {showSavePreset ? (
            <span className="inline-flex items-center gap-1">
              <input autoFocus value={presetName} onChange={(e) => setPresetName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") savePreset(); if (e.key === "Escape") { setShowSavePreset(false); setPresetName(""); } }}
                placeholder="Preset name…" data-testid="outreach-preset-name"
                className="rounded-full border border-[#c9a961] bg-white px-3 py-1 text-[11px] focus:outline-none w-36" />
              <button type="button" onClick={savePreset} data-testid="outreach-preset-save-confirm"
                className="inline-flex items-center gap-1 rounded-full bg-[#1e3d3a] text-white px-2.5 py-1 text-[10px] uppercase tracking-[0.14em] hover:bg-[#c9a961] hover:text-[#1e3d3a] transition-colors duration-300">
                <Save className="h-3 w-3" /> Save
              </button>
            </span>
          ) : (
            <button type="button" onClick={() => setShowSavePreset(true)} data-testid="outreach-preset-save"
              className="inline-flex items-center gap-1 rounded-full border border-[#c9a961]/50 text-[#a3823f] px-3 py-1 text-[10px] uppercase tracking-[0.14em] hover:bg-[#c9a961]/12 transition-colors duration-300">
              <Save className="h-3 w-3" /> Save current
            </button>
          )}
        </div>

        {scheduleEditor && (() => {
          const p = presets.find((x) => x.id === scheduleEditor);
          if (!p) return null;
          return <ScheduleEditor key={p.id} preset={p} onSave={saveSchedule} onCancel={() => setScheduleEditor(null)} />;
        })()}

        {upcoming.length > 0 && (
          <div data-testid="outreach-upcoming" className="mb-4 rounded-xl border border-[#1e3d3a]/12 bg-[#eae7de]/30 px-4 py-3">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-[#1e3d3a]/55 mb-2">
              <Clock className="h-3.5 w-3.5 text-[#6b7c3a]" /> Upcoming runs
            </div>
            <ul className="space-y-1.5">
              {upcoming.map((u) => (
                <li key={u.id} data-testid={`outreach-upcoming-${u.id}`} className="flex flex-wrap items-center justify-between gap-2 text-xs">
                  <span className="text-[#1e3d3a] font-medium">{u.name}
                    {u.cities > 1 && <span className="ml-1 text-[#a3823f]">· {u.cities} cities</span>}
                  </span>
                  <span className="text-[#4d5a24]">
                    {u.recurrence === "Every day" ? "Daily" : u.recurrence} · {u.next_local}
                    <span className="text-[#1e3d3a]/45"> ({u.tz.split("/").pop().replace("_", " ")})</span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="mb-3 flex flex-wrap items-center gap-2">
          <span className="text-[10px] uppercase tracking-[0.16em] text-[#1e3d3a]/50">Source</span>
          {[
            { k: "both", label: "BBB + Internet" },
            { k: "web", label: "Internet search" },
            { k: "bbb", label: "BBB feed" },
          ].map(({ k, label }) => (
            <button key={k} type="button" data-testid={`outreach-source-${k}`} onClick={() => setFind((f) => ({ ...f, source: k }))}
              className={`rounded-full border px-3 py-1 text-[11px] transition-colors duration-300 ${find.source === k ? "border-[#1e3d3a] bg-[#1e3d3a] text-white" : "border-[#1e3d3a]/15 text-[#1e3d3a]/55 hover:border-[#c9a961]/50"}`}>
              {label}
            </button>
          ))}
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <input data-testid="outreach-find-keyword" value={find.keyword} onChange={(e) => setFind((f) => ({ ...f, keyword: e.target.value }))}
            placeholder="Industry / keyword (blank = all categories)"
            className="rounded-xl border border-[#1e3d3a]/15 bg-white px-4 py-2.5 text-sm focus:border-[#c9a961] focus:outline-none" />
          <input data-testid="outreach-find-location" value={find.location} onChange={(e) => setFind((f) => ({ ...f, location: e.target.value }))}
            placeholder="Location, e.g. Dallas, TX"
            className="rounded-xl border border-[#1e3d3a]/15 bg-white px-4 py-2.5 text-sm focus:border-[#c9a961] focus:outline-none" />
        </div>
        <div className="mt-3 grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-[0.16em] text-[#1e3d3a]/50">Sweep multiple cities (optional)</label>
            <input data-testid="outreach-find-cities" value={find.cities} onChange={(e) => setFind((f) => ({ ...f, cities: e.target.value }))}
              placeholder="Dallas TX, Austin TX, Houston TX"
              className="mt-1 w-full rounded-xl border border-[#1e3d3a]/15 bg-white px-4 py-2.5 text-sm focus:border-[#c9a961] focus:outline-none" />
            {citiesArr(find.cities).length > 1 && (
              <p className="mt-1 text-[11px] text-[#a3823f]">Will sweep {citiesArr(find.cities).length} cities this run.</p>
            )}
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.16em] text-[#1e3d3a]/50">Search radius (miles)</label>
            <input type="number" min="0" max="80" step="5" data-testid="outreach-find-radius" value={find.radius_mi}
              onChange={(e) => setFind((f) => ({ ...f, radius_mi: e.target.value }))}
              placeholder="0 = exact city"
              className="mt-1 w-full rounded-xl border border-[#1e3d3a]/15 bg-white px-4 py-2.5 text-sm focus:border-[#c9a961] focus:outline-none" />
            <p className="mt-1 text-[11px] text-[#1e3d3a]/45">0 = city only · higher = metro + nearby suburbs (same state)</p>
          </div>
        </div>
        <p className="mt-2 text-[11px] text-[#6b7c3a]" data-testid="outreach-dedupe-note">
          ✓ Businesses already in your pipeline are skipped automatically — repeat runs only surface fresh leads.
        </p>
        <div className="mt-3 flex flex-wrap gap-1.5">
          {find.source !== "web" && RATING_OPTIONS.map((r) => (
            <button key={r} type="button" data-testid={`outreach-rating-${r}`} onClick={() => toggleRating(r)}
              className={`rounded-full border px-2.5 py-1 text-[11px] transition-colors duration-300 ${find.ratings.includes(r) ? "border-[#b23a48]/50 bg-[#b23a48]/8 text-[#b23a48]" : "border-[#1e3d3a]/15 text-[#1e3d3a]/50 hover:border-[#c9a961]/50"}`}>
              {r}
            </button>
          ))}
        </div>
        {find.source !== "bbb" && (
          <div className="mt-3 flex flex-wrap items-center gap-4" data-testid="outreach-quality-filters">
            <div className="flex items-center gap-2">
              <label className="text-[10px] uppercase tracking-[0.16em] text-[#1e3d3a]/50">Min reviews</label>
              <input type="number" min="0" data-testid="outreach-find-min-reviews" value={find.min_reviews}
                onChange={(e) => setFind((f) => ({ ...f, min_reviews: e.target.value }))}
                className="w-20 rounded-lg border border-[#1e3d3a]/15 bg-white px-2.5 py-1.5 text-sm focus:border-[#c9a961] focus:outline-none" />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-[10px] uppercase tracking-[0.16em] text-[#1e3d3a]/50">Min rating</label>
              <input type="number" min="0" max="5" step="0.1" data-testid="outreach-find-min-rating" value={find.min_rating}
                onChange={(e) => setFind((f) => ({ ...f, min_rating: e.target.value }))}
                className="w-20 rounded-lg border border-[#1e3d3a]/15 bg-white px-2.5 py-1.5 text-sm focus:border-[#c9a961] focus:outline-none" />
              <span className="text-[11px] text-[#1e3d3a]/40">/ 5 ★</span>
            </div>
            <span className="text-[11px] text-[#1e3d3a]/40">Only established, actively-operating businesses</span>
          </div>
        )}
        {find.source !== "bbb" && (
          <div className="mt-3 grid sm:grid-cols-2 gap-3" data-testid="outreach-category-filters">
            <div>
              <label className="text-[10px] uppercase tracking-[0.16em] text-[#1e3d3a]/50">Include categories</label>
              <input type="text" data-testid="outreach-include-categories" value={find.include_categories}
                onChange={(e) => setFind((f) => ({ ...f, include_categories: e.target.value }))}
                placeholder="e.g. HVAC contractor, Plumber (comma-separated)"
                className="mt-1 w-full rounded-lg border border-[#1e3d3a]/15 bg-white px-3 py-2 text-sm focus:border-[#c9a961] focus:outline-none" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.16em] text-[#1e3d3a]/50">Exclude categories</label>
              <input type="text" data-testid="outreach-exclude-categories" value={find.exclude_categories}
                onChange={(e) => setFind((f) => ({ ...f, exclude_categories: e.target.value }))}
                placeholder="e.g. Franchise, Corporate office"
                className="mt-1 w-full rounded-lg border border-[#1e3d3a]/15 bg-white px-3 py-2 text-sm focus:border-[#c9a961] focus:outline-none" />
            </div>
          </div>
        )}
        <div className="mt-4 flex flex-wrap gap-2">
          <button type="button" data-testid="outreach-find-btn" onClick={findLeads} disabled={finding}
            className="inline-flex items-center gap-2 rounded-full border border-[#1e3d3a]/20 text-[#1e3d3a] px-4 py-2 text-xs uppercase tracking-[0.18em] hover:border-[#c9a961] hover:text-[#a3823f] disabled:opacity-60 transition-colors duration-300">
            {finding ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />} Find leads only
          </button>
          <button type="button" data-testid="outreach-find-run-btn" onClick={runEngine} disabled={running}
            className="inline-flex items-center gap-2 rounded-full bg-[#1e3d3a] text-white px-4 py-2 text-xs uppercase tracking-[0.18em] hover:bg-[#c9a961] hover:text-[#1e3d3a] disabled:opacity-60 transition-colors duration-300">
            {running ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} Find + analyze + draft
          </button>
        </div>
        </>)}
      </div>

      <SmsTemplateLibrary collapsed={!!collapsed.smsLib} onToggle={() => toggleCollapse("smsLib")}
        onUse={async (text) => { await saveConfig({ sms_template: text }); toast.success("Loaded into your SMS template."); }} />

      {/* Status filter */}
      <div ref={leadsListRef} className="flex flex-wrap gap-1.5 scroll-mt-24" data-testid="outreach-status-filter">
        {["all", "found", "analyzed", "queued", "needs_email", "sent", "replied", "positive_reply", "meeting", "converted", "opted_out", "dismissed"].map((s) => (
          <button key={s} type="button" data-testid={`outreach-filter-${s}`} onClick={() => setStatusFilter(s)}
            className={`rounded-full border px-3 py-1 text-[11px] uppercase tracking-[0.14em] transition-colors duration-300 ${statusFilter === s ? "border-[#1e3d3a] bg-[#1e3d3a] text-white" : "border-[#1e3d3a]/15 text-[#1e3d3a]/55 hover:border-[#c9a961]/50"}`}>
            {s === "all" ? "All" : (STATUS_META[s]?.label || s)}
          </button>
        ))}
      </div>

      {/* Founder (owner) filter */}
      {founders.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5" data-testid="outreach-owner-filter">
          <span className="text-[10px] uppercase tracking-[0.18em] text-[#1e3d3a]/40 mr-1">Founder</span>
          {[{ v: "all", label: "Everyone" }, ...founders.map((f) => ({ v: f.username, label: f.name })), { v: "__unassigned", label: "Unassigned" }].map((o) => (
            <button key={o.v} type="button" data-testid={`outreach-owner-${o.v}`} onClick={() => setOwnerFilter(o.v)}
              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] tracking-[0.02em] transition-colors duration-300 ${ownerFilter === o.v ? "border-[#a3823f] bg-[#a3823f]/12 text-[#a3823f]" : "border-[#1e3d3a]/15 text-[#1e3d3a]/55 hover:border-[#c9a961]/50"}`}>
              {o.v !== "all" && o.v !== "__unassigned" && <span className="h-2 w-2 rounded-full" style={{ background: founderColor(o.v) }} />}
              {o.label}
            </button>
          ))}
        </div>
      )}

      {/* Mass send */}
      {leads.length > 0 && (
        <div data-testid="outreach-mass-send" className="rounded-2xl border border-[#c9a961]/40 bg-gradient-to-r from-[#c9a961]/10 to-transparent px-5 py-3">
          <div className="flex flex-wrap items-center gap-3">
            <button type="button" data-testid="outreach-collapse-masssend" onClick={() => toggleCollapse("massSend")}
              className="flex items-center gap-2 text-[#1e3d3a]/70 hover:text-[#a3823f] transition-colors duration-300">
              {collapsed.massSend ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
              <Zap className="h-4 w-4 text-[#a3823f]" />
              <span className="text-[11px] uppercase tracking-[0.18em]">Mass send now</span>
            </button>
            <span className="text-[11px] text-[#1e3d3a]/50">
              {leads.filter((l) => ["queued", "drafted"].includes(l.status)).length} ready to email ·{" "}
              {leads.filter((l) => l.phone && l.sms_status !== "sent" && !["dismissed", "converted", "opted_out"].includes(l.status)).length} textable
            </span>
            {!collapsed.massSend && (
            <div className="ml-auto flex items-center gap-2">
              <button type="button" data-testid="outreach-mass-schedule-toggle" onClick={() => setShowSchedule((s) => !s)}
                className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-2 text-[10px] uppercase tracking-[0.16em] transition-colors duration-300 ${config.mass_send_schedule?.enabled ? "border-[#6b7c3a]/50 bg-[#6b7c3a]/10 text-[#4d5a24]" : "border-[#1e3d3a]/20 text-[#1e3d3a]/60 hover:border-[#c9a961] hover:text-[#a3823f]"}`}>
                <Clock className="h-3.5 w-3.5" /> {config.mass_send_schedule?.enabled ? "Scheduled" : "Schedule"}
              </button>
              <button type="button" data-testid="outreach-mass-email" onClick={() => massSend("email")} disabled={!!massSending}
                className="inline-flex items-center gap-1.5 rounded-full bg-[#1e3d3a] text-white px-4 py-2 text-[10px] uppercase tracking-[0.16em] hover:bg-[#c9a961] hover:text-[#1e3d3a] disabled:opacity-50 transition-colors duration-300">
                {massSending === "email" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5" />} Send all emails
              </button>
              <button type="button" data-testid="outreach-mass-sms" onClick={() => massSend("sms")} disabled={!!massSending}
                title={smsStatus.configured ? "Text every lead with a phone number" : "Connect Twilio to enable"}
                className="inline-flex items-center gap-1.5 rounded-full border border-[#1e3d3a]/25 text-[#1e3d3a] px-4 py-2 text-[10px] uppercase tracking-[0.16em] hover:border-[#c9a961] hover:text-[#a3823f] disabled:opacity-50 transition-colors duration-300">
                {massSending === "sms" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <MessageSquare className="h-3.5 w-3.5" />} Text all leads
              </button>
            </div>
            )}
          </div>
          {!collapsed.massSend && (() => {
            const textable = leads.filter((l) => l.phone && l.sms_status !== "sent" && !["dismissed", "converted", "opted_out"].includes(l.status)).length;
            if (!textable || !smsStatus.configured) return null;
            const seg = smsSegments(config.sms_template) || 2;
            const est = SMS_RATE_PER_SEG * seg * textable;
            return (
              <p className="mt-2 text-[11px] text-[#a3823f] flex flex-wrap items-center gap-1.5" data-testid="outreach-mass-sms-cost">
                <MessageSquare className="h-3.5 w-3.5" />
                Texting all {textable} leads ≈ <span className="font-semibold">${est.toFixed(2)}</span>
                <span className="text-[#1e3d3a]/45">(~{seg} segment{seg === 1 ? "" : "s"} × ${SMS_RATE_PER_SEG.toFixed(4)} each · est.)</span>
                {smsStatus.spend?.texts > 0 && (
                  <span className="text-[#4d5a24]" data-testid="outreach-sms-actual-spend">· spent so far <span className="font-semibold">${(smsStatus.spend.cost || 0).toFixed(2)}</span> across {smsStatus.spend.texts} text{smsStatus.spend.texts === 1 ? "" : "s"}</span>
                )}
              </p>
            );
          })()}
          {!collapsed.massSend && config.mass_send_schedule?.enabled && !showSchedule && (
            <p className="mt-2 text-[11px] text-[#4d5a24]" data-testid="outreach-mass-next">
              ⏱ Auto-{config.mass_send_schedule.channel === "sms" ? "text" : "email"} blast {config.mass_send_schedule.day === "daily" ? "every day" : "weekly"} at {String(config.mass_send_schedule.hour).padStart(2, "0")}:{String(config.mass_send_schedule.minute).padStart(2, "0")} {config.mass_send_schedule.tz?.replace("_", " ")}{schedNext ? ` · next ${schedNext}` : ""}
            </p>
          )}
          {!collapsed.massSend && showSchedule && (
            <MassSendScheduler schedule={config.mass_send_schedule} smsConfigured={smsStatus.configured}
              onSave={saveSched} onClose={() => setShowSchedule(false)} />
          )}
        </div>
      )}

      {/* Leads list */}
      <div className="rounded-2xl border border-[#1e3d3a]/12 bg-white p-2 sm:p-4">
        {(() => {
          const visibleLeads = leads.filter((l) => ownerFilter === "all" || (ownerFilter === "__unassigned" ? !l.owner : l.owner === ownerFilter));
          const unassignedVisible = visibleLeads.filter((l) => !l.owner);
          const totalUnassigned = leads.filter((l) => !l.owner && !["converted", "dismissed", "opted_out"].includes(l.status)).length;
          const allUnassignedSelected = unassignedVisible.length > 0 && unassignedVisible.every((l) => selected.has(l.id));
          return (
          <>
          {/* Bulk assign toolbar */}
          {(selected.size > 0 || totalUnassigned > 0) && (
            <div data-testid="outreach-bulk-assign" className="flex flex-wrap items-center gap-2 rounded-xl border border-[#1e3d3a]/12 bg-[#eae7de]/50 px-3 py-2 mb-3">
              {unassignedVisible.length > 0 && (
                <button type="button" data-testid="outreach-bulk-select-unassigned"
                  onClick={() => setSelected((prev) => {
                    const next = new Set(prev);
                    if (allUnassignedSelected) unassignedVisible.forEach((l) => next.delete(l.id));
                    else unassignedVisible.forEach((l) => next.add(l.id));
                    return next;
                  })}
                  className="inline-flex items-center gap-1.5 rounded-full border border-[#1e3d3a]/20 text-[#1e3d3a]/70 px-3 py-1 text-[10px] uppercase tracking-[0.14em] hover:border-[#c9a961] hover:text-[#a3823f]">
                  <CheckSquare className="h-3.5 w-3.5" /> {allUnassignedSelected ? "Deselect" : "Select"} unassigned ({unassignedVisible.length})
                </button>
              )}
              <span className="text-[11px] text-[#1e3d3a]/60" data-testid="outreach-bulk-count">
                {selected.size > 0 ? `${selected.size} selected` : `${totalUnassigned} unassigned lead${totalUnassigned === 1 ? "" : "s"}`}
              </span>
              <div className="ml-auto flex flex-wrap items-center gap-1.5">
                <span className="text-[10px] uppercase tracking-[0.16em] text-[#1e3d3a]/40">Assign {selected.size > 0 ? "selected" : "all unassigned"} to</span>
                {founders.map((f) => (
                  <button key={f.username} type="button" data-testid={`outreach-bulk-assign-${f.username}`}
                    disabled={bulkBusy}
                    onClick={() => bulkAssign(f.username, selected.size > 0 ? { lead_ids: [...selected] } : { all_unassigned: true })}
                    className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.14em] font-semibold text-white hover:opacity-90 disabled:opacity-50 transition-opacity duration-300"
                    style={{ background: founderColor(f.username) }}>
                    {bulkBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <span className="h-2 w-2 rounded-full bg-white/80" />} {f.name}
                  </button>
                ))}
                {selected.size > 0 && (
                  <button type="button" data-testid="outreach-bulk-clear" onClick={clearSelection}
                    className="inline-flex items-center rounded-full border border-[#1e3d3a]/15 text-[#1e3d3a]/45 px-2.5 py-1 text-[10px] hover:text-[#b23a48] hover:border-[#b23a48]/30">
                    Clear
                  </button>
                )}
              </div>
            </div>
          )}
          {visibleLeads.length === 0 && (
            <p data-testid="outreach-empty" className="text-xs text-[#1e3d3a]/55 py-6 text-center">
              No leads yet. Run the engine or use the lead finder above.
            </p>
          )}
          <ul className="divide-y divide-[#1e3d3a]/8">
            {visibleLeads.map((l) => (
              <LeadRow key={l.id} lead={l} expanded={expanded === l.id}
                onToggle={() => setExpanded(expanded === l.id ? null : l.id)}
                smsConfigured={smsStatus.configured}
                selected={selected.has(l.id)} onSelectToggle={() => toggleSelect(l.id)}
                founders={founders} founderName={founderName} founderColor={founderColor} onReassign={reassignOwner}
                onPatch={patchLead} onReload={() => { loadDash(); loadLeads(); }} onResearch={onResearch} />
            ))}
          </ul>
          </>
          );
        })()}
      </div>
    </div>
  );
}

const SMS_RATE_PER_SEG = 0.0105; // US Twilio ~$0.0079 + ~$0.003 A2P carrier fee
const smsSegments = (t) => {
  const s = (t || "").length;
  if (!s) return 0;
  return s <= 160 ? 1 : Math.ceil(s / 153);
};
const smsCostUsd = (t, n = 1) => smsSegments(t) * SMS_RATE_PER_SEG * n;

const SMS_TEMPLATES = [
  { id: "A", kind: "Pitch", chars: 216,
    text: "Hi {{first_name}}, this is Kalib with Elevare Solutions. We help {{industry}} businesses cut costs & lift their BBB score. Could I share one quick idea for {{business_name}}? Reply STOP to opt out, HELP for help. Msg&data rates may apply." },
  { id: "B", kind: "Pitch", chars: 214,
    text: "{{first_name}}, Kalib here from Elevare Solutions—we help {{business_name}} trim expenses & repair its BBB reputation, often finding real savings fast. Open to a quick idea? Reply STOP to opt out, HELP for help. Msg&data rates may apply." },
  { id: "C", kind: "Pitch", chars: 333,
    text: "Hi {{first_name}}, Kalib with Elevare Solutions here. We help {{industry}} businesses like {{business_name}} cut wasted costs, fix billing leaks & raise a low BBB score—often finding real monthly savings on the first review. Can I send one specific idea, no obligation? Reply STOP to opt out, HELP for help. Msg&data rates may apply." },
  { id: "D", kind: "Pitch", chars: 499,
    text: "Hi {{first_name}}, this is Kalib with Elevare Solutions. We work with {{industry}} businesses like {{business_name}} to regain financial control—cutting wasted spend, fixing billing leaks, tightening vendor costs, and repairing a low or failing BBB score. Most owners we review are quietly losing money each month, and we usually spot real savings on the first look. Could I send one specific, no-obligation idea for {{business_name}}? Reply STOP to opt out, HELP for help. Msg&data rates may apply." },
  { id: "A", kind: "Opt-in", chars: 267,
    text: "Hi {{first_name}}, this is Kalib with Elevare Solutions. We help {{industry}} businesses cut costs & lift their BBB score. Reply YES and I will send one quick idea for {{business_name}}. Msg freq varies, msg&data rates may apply. Reply STOP to opt out, HELP for help." },
  { id: "B", kind: "Opt-in", chars: 237,
    text: "{{first_name}}, Kalib here from Elevare Solutions—we help {{business_name}} trim expenses & repair its BBB reputation. Reply YES for one quick savings idea. Msg freq varies, msg&data rates may apply. Reply STOP to opt out, HELP for help." },
  { id: "C", kind: "Opt-in", chars: 336,
    text: "Hi {{first_name}}, Kalib with Elevare Solutions here. We help {{industry}} businesses like {{business_name}} cut wasted costs, fix billing leaks & raise a low BBB score—often finding real monthly savings. Reply YES to get one specific idea, no obligation. Msg freq varies, msg&data rates may apply. Reply STOP to opt out, HELP for help." },
  { id: "D", kind: "Opt-in", chars: 412,
    text: "Hi {{first_name}}, this is Kalib with Elevare Solutions. We work with {{industry}} businesses like {{business_name}} to regain financial control—cutting wasted spend, fixing billing leaks, tightening vendor costs & repairing a low BBB score. Reply YES and I will text one specific, no-obligation savings idea for {{business_name}}. Msg freq varies, msg&data rates may apply. Reply STOP to opt out, HELP for help." },
];

const EMAIL_STATUS_META = {
  valid: { label: "Valid", cls: "bg-[#4b7a3a]/12 text-[#4d5a24] border-[#4b7a3a]/40", dot: "bg-[#4b7a3a]" },
  risky: { label: "Risky", cls: "bg-[#c9a961]/15 text-[#a3823f] border-[#c9a961]/50", dot: "bg-[#c9a961]" },
  invalid: { label: "Invalid", cls: "bg-[#b23a48]/10 text-[#b23a48] border-[#b23a48]/40", dot: "bg-[#b23a48]" },
};
function EmailBadge({ status }) {
  const s = EMAIL_STATUS_META[status];
  if (!s) return null;
  return <span className={`rounded-full border px-1.5 py-0.5 text-[9px] uppercase tracking-[0.1em] ${s.cls}`}>{s.label}</span>;
}

function SmsTemplateLibrary({ collapsed, onToggle, onUse }) {
  const [copied, setCopied] = useState(null);
  const [using, setUsing] = useState(null);
  const copy = async (t, key) => {
    try {
      await navigator.clipboard.writeText(t);
      setCopied(key);
      toast.success("Message copied to clipboard.");
      setTimeout(() => setCopied((c) => (c === key ? null : c)), 2000);
    } catch {
      toast.error("Couldn't copy — select the text and copy manually.");
    }
  };
  const use = async (t, key) => {
    if (!onUse) return;
    setUsing(key);
    try { await onUse(t); } finally { setUsing(null); }
  };
  return (
    <div className="rounded-2xl border border-[#1e3d3a]/12 bg-white p-5" data-testid="outreach-sms-templates">
      <button type="button" data-testid="outreach-collapse-smslib" onClick={onToggle}
        className="flex w-full items-center justify-between gap-2 text-[10px] uppercase tracking-[0.24em] text-[#c9a961] hover:text-[#a3823f] transition-colors duration-300">
        <span className="inline-flex items-center gap-1.5"><FileText className="h-3.5 w-3.5" /> Compliant SMS templates · copy &amp; paste</span>
        {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
      </button>
      {!collapsed && (
        <div className="mt-4 space-y-3">
          <p className="text-[11px] text-[#1e3d3a]/55">
            Ready-to-use messages that meet your <a href="/sms-terms" target="_blank" rel="noreferrer" className="text-[#a3823f] hover:underline">Campaign Terms of Service</a> (STOP / HELP / rates). Placeholders auto-fill per lead.
          </p>
          {SMS_TEMPLATES.map((t, i) => {
            const key = `${t.kind}-${t.id}-${i}`;
            return (
              <div key={key} data-testid={`sms-template-${key}`} className="rounded-xl border border-[#1e3d3a]/12 bg-[#eae7de]/40 p-3">
                <div className="flex items-center justify-between gap-2 mb-1.5">
                  <span className="inline-flex items-center gap-2">
                    <span className={`rounded-full px-2 py-0.5 text-[9px] uppercase tracking-[0.14em] ${t.kind === "Opt-in" ? "bg-[#4b7a3a]/12 text-[#4d5a24]" : "bg-[#1e3d3a]/8 text-[#1e3d3a]/60"}`}>{t.kind} {t.id}</span>
                    <span className="text-[10px] text-[#1e3d3a]/40">{t.chars} chars</span>
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                  <button type="button" data-testid={`sms-template-use-${key}`} onClick={() => use(t.text, key)}
                    className="inline-flex items-center gap-1 rounded-full border border-[#6b7c3a]/40 bg-[#6b7c3a]/8 px-3 py-1 text-[10px] uppercase tracking-[0.14em] text-[#4d5a24] hover:bg-[#6b7c3a]/16 transition-colors duration-300">
                    {using === key ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />} Use this
                  </button>
                  <button type="button" data-testid={`sms-template-copy-${key}`} onClick={() => copy(t.text, key)}
                    className="inline-flex items-center gap-1 rounded-full border border-[#1e3d3a]/15 px-3 py-1 text-[10px] uppercase tracking-[0.14em] text-[#1e3d3a]/70 hover:border-[#c9a961] hover:text-[#a3823f] transition-colors duration-300">
                    {copied === key ? <><Check className="h-3 w-3 text-[#4b7a3a]" /> Copied</> : <><Copy className="h-3 w-3" /> Copy</>}
                  </button>
                  </span>
                </div>
                <p className="text-sm text-[#1e3d3a] leading-relaxed whitespace-pre-wrap break-words select-all">{t.text}</p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function LeadRow({ lead, expanded, onToggle, onPatch, onReload, onResearch, smsConfigured, selected = false, onSelectToggle, founders = [], founderName = (u) => u, founderColor = () => "#9a9a9a", onReassign }) {
  const meta = STATUS_META[lead.status] || STATUS_META.found;
  const [email, setEmail] = useState(lead.contact_email || "");
  const [subject, setSubject] = useState(lead.draft?.subject || "");
  const [body, setBody] = useState(lead.draft?.body || "");
  const [busy, setBusy] = useState(false);
  const [smsBody, setSmsBody] = useState(lead.sms_draft || lead.sms_body || "");
  const [smsBusy, setSmsBusy] = useState(false);
  const [replyBody, setReplyBody] = useState("");
  const [replyBusy, setReplyBusy] = useState(false);
  const replyRef = useRef(null);

  const hasReplied = !!(lead.reply_snippet || lead.sms_reply_snippet || ["replied", "positive_reply"].includes(lead.status));
  const replyChannel = (() => {
    const e = lead.replied_at, s = lead.sms_replied_at;
    if (e && s) return String(e) >= String(s) ? "email" : "sms";
    if (e) return "email";
    if (s) return "sms";
    return (lead.contact_email || "").includes("@") ? "email" : "sms";
  })();

  const sendLeadReply = async () => {
    if (!replyBody.trim()) return;
    setReplyBusy(true);
    try {
      const { data } = await api.post(`/admin/outreach/leads/${lead.id}/reply`, { body: replyBody, channel: replyChannel });
      toast.success(data.channel === "sms" ? "Text reply sent." : "Email reply sent.");
      setReplyBody(""); onReload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Reply failed."); }
    finally { setReplyBusy(false); }
  };

  useEffect(() => {
    setEmail(lead.contact_email || "");
    setSubject(lead.draft?.subject || "");
    setBody(lead.draft?.body || "");
    setSmsBody(lead.sms_draft || lead.sms_body || "");
  }, [lead.id, lead.draft, lead.contact_email, lead.sms_draft, lead.sms_body]);

  const a = lead.analysis || {};

  const genSms = async () => {
    setSmsBusy(true);
    try {
      const { data } = await api.post(`/admin/outreach/leads/${lead.id}/sms/draft`);
      setSmsBody(data.sms_draft || "");
      toast.success("Text drafted.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not draft text."); }
    finally { setSmsBusy(false); }
  };

  const sendSms = async () => {
    if (!smsConfigured) { toast.error("Connect Twilio first to send text messages."); return; }
    setSmsBusy(true);
    try {
      await api.post(`/admin/outreach/leads/${lead.id}/sms/send`, { body: smsBody });
      toast.success("Text message sent.");
      onReload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Text send failed."); }
    finally { setSmsBusy(false); }
  };

  const saveDraft = async () => { await onPatch(lead.id, { subject, body, contact_email: email }); };

  const approve = async () => {
    setBusy(true);
    await onPatch(lead.id, { subject, body, contact_email: email }, true);
    try {
      await api.post(`/admin/outreach/leads/${lead.id}/approve`);
      toast.success("Sent from your inbox.");
      onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed.");
    } finally { setBusy(false); }
  };

  const gen = async (kind) => {
    setBusy(true);
    try {
      await api.post(`/admin/outreach/leads/${lead.id}/${kind}`);
      toast.success(kind === "analyze" ? "Analyzed & scored." : "Draft generated.");
      onReload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed."); }
    finally { setBusy(false); }
  };

  const mark = async (status, msg) => {
    try { await api.post(`/admin/outreach/leads/${lead.id}/mark`, { status }); toast.success(msg); onReload(); }
    catch { toast.error("Failed."); }
  };
  const dismiss = async () => {
    try { await api.post(`/admin/outreach/leads/${lead.id}/dismiss`); toast.message("Dismissed."); onReload(); }
    catch { toast.error("Failed."); }
  };

  return (
    <li data-testid={`outreach-lead-${lead.id}`} className="py-3 px-1">
      <div className="flex items-start gap-3">
        {onSelectToggle && (
          <button type="button" onClick={onSelectToggle} data-testid={`outreach-select-${lead.id}`}
            title={selected ? "Deselect lead" : "Select lead"}
            className={`mt-0.5 transition-colors duration-200 ${selected ? "text-[#a3823f]" : "text-[#1e3d3a]/30 hover:text-[#a3823f]"}`}>
            {selected ? <CheckSquare className="h-4 w-4" /> : <Square className="h-4 w-4" />}
          </button>
        )}
        <button type="button" onClick={onToggle} className="mt-0.5 text-[#1e3d3a]/40 hover:text-[#a3823f]" data-testid={`outreach-toggle-${lead.id}`}>
          {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-[#1e3d3a] truncate">{lead.business_name}</span>
            {typeof lead.opportunity_score === "number" && (
              <span data-testid={`outreach-score-${lead.id}`} className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${scoreColor(lead.opportunity_score)}`}>
                {lead.opportunity_score}/100
              </span>
            )}
            <span className={`rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-[0.12em] ${meta.color}`} data-testid={`outreach-status-${lead.id}`}>{meta.label}</span>
            {lead.category && <span className="text-[10px] text-[#1e3d3a]/50">{lead.category}</span>}
            {typeof lead.maps_rating === "number" && (
              <span className="text-[10px] text-[#a3823f]" data-testid={`outreach-rating-${lead.id}`}>{lead.maps_rating}★{lead.maps_reviews ? ` (${lead.maps_reviews})` : ""}</span>
            )}
            {lead.bbb_rating && <span className="text-[10px] text-[#1e3d3a]/45">BBB {lead.bbb_rating}</span>}
            {lead.owner && (
              <span data-testid={`outreach-owner-badge-${lead.id}`}
                className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium text-white"
                style={{ background: founderColor(lead.owner) }}>
                {founderName(lead.owner)}
              </span>
            )}
          </div>
          <div className="mt-0.5 text-[11px] text-[#1e3d3a]/55 truncate">
            {lead.location}{lead.phone ? ` · ${lead.phone}` : ""}{lead.contact_email ? ` · ${lead.contact_email}` : lead.contact_name ? ` · ${lead.contact_name}` : ""}
          </div>
        </div>
        <div className="shrink-0 flex items-center gap-1">
          {hasReplied && (
            <button type="button" data-testid={`outreach-reply-${lead.id}`}
              onClick={() => {
                if (!expanded) onToggle();
                setTimeout(() => {
                  replyRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
                  replyRef.current?.focus();
                }, expanded ? 50 : 350);
              }}
              title={`Reply to this prospect by ${replyChannel === "sms" ? "text" : "email"}`}
              className="inline-flex items-center gap-1 rounded-full border border-[#6b7c3a]/50 bg-[#6b7c3a]/8 text-[#4d5a24] px-2.5 py-1 text-[10px] uppercase tracking-[0.14em] hover:bg-[#6b7c3a]/16 transition-colors duration-300">
              <Reply className="h-3 w-3" /> Reply
            </button>
          )}
          {onResearch && (
            <button type="button" data-testid={`outreach-research-${lead.id}`}
              onClick={() => onResearch(lead.business_name, lead.location)}
              title="Open full BI Assessment in Prospect Research"
              className="inline-flex items-center gap-1 rounded-full border border-[#1e3d3a]/20 text-[#1e3d3a]/70 px-2.5 py-1 text-[10px] uppercase tracking-[0.14em] hover:border-[#c9a961] hover:text-[#a3823f] transition-colors duration-300">
              <FileSearch className="h-3 w-3" /> Research
            </button>
          )}
          {(lead.status === "found") && (
            <button type="button" data-testid={`outreach-analyze-${lead.id}`} onClick={() => gen("analyze")} disabled={busy}
              className="inline-flex items-center gap-1 rounded-full border border-[#1e3d3a]/15 text-[#1e3d3a]/65 px-2.5 py-1 text-[10px] uppercase tracking-[0.14em] hover:border-[#c9a961] hover:text-[#a3823f] disabled:opacity-50">
              {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />} Analyze
            </button>
          )}
          {(lead.status === "analyzed") && (
            <button type="button" data-testid={`outreach-draft-${lead.id}`} onClick={() => gen("draft")} disabled={busy}
              className="inline-flex items-center gap-1 rounded-full border border-[#c9a961]/50 bg-[#c9a961]/8 text-[#a3823f] px-2.5 py-1 text-[10px] uppercase tracking-[0.14em] hover:bg-[#c9a961]/16 disabled:opacity-50">
              {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Mail className="h-3 w-3" />} Draft
            </button>
          )}
          {(lead.status === "queued" || lead.status === "needs_email") && (
            <button type="button" data-testid={`outreach-approve-${lead.id}`} onClick={approve} disabled={busy}
              className="inline-flex items-center gap-1 rounded-full border border-[#6b7c3a]/50 bg-[#6b7c3a]/8 text-[#4d5a24] px-2.5 py-1 text-[10px] uppercase tracking-[0.14em] hover:bg-[#6b7c3a]/16 disabled:opacity-50">
              {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />} Approve & send
            </button>
          )}
          {(lead.status === "replied" || lead.status === "positive_reply") && (
            <button type="button" data-testid={`outreach-meeting-${lead.id}`} onClick={() => mark("meeting", "Marked as meeting booked.")}
              className="inline-flex items-center gap-1 rounded-full border border-[#1e3d3a]/20 text-[#1e3d3a] px-2.5 py-1 text-[10px] uppercase tracking-[0.14em] hover:border-[#c9a961]">
              <CalendarCheck className="h-3 w-3" /> Meeting
            </button>
          )}
          {lead.status === "meeting" && (
            <button type="button" data-testid={`outreach-convert-${lead.id}`} onClick={() => mark("converted", "Marked as converted client.")}
              className="inline-flex items-center gap-1 rounded-full border border-[#a3823f]/40 bg-[#c9a961]/10 text-[#a3823f] px-2.5 py-1 text-[10px] uppercase tracking-[0.14em] hover:bg-[#c9a961]/20">
              <Trophy className="h-3 w-3" /> Converted
            </button>
          )}
          {lead.phone && lead.status !== "dismissed" && lead.status !== "converted" && (
            <button type="button" data-testid={`outreach-text-${lead.id}`}
              onClick={() => { if (!expanded) onToggle(); if (!smsBody) genSms(); }}
              title={smsConfigured ? "Draft & send a text to this lead" : "Draft a text (connect Twilio to send)"}
              className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[10px] uppercase tracking-[0.14em] transition-colors duration-300 ${lead.sms_status === "sent" ? "border-[#6b7c3a]/50 bg-[#6b7c3a]/10 text-[#4d5a24]" : "border-[#1e3d3a]/15 text-[#1e3d3a]/65 hover:border-[#c9a961] hover:text-[#a3823f]"}`}>
              <MessageSquare className="h-3 w-3" /> {lead.sms_status === "sent" ? "Texted" : "Text"}
            </button>
          )}
          {lead.status !== "dismissed" && lead.status !== "converted" && (
            <button type="button" data-testid={`outreach-dismiss-${lead.id}`} onClick={dismiss}
              className="inline-flex items-center rounded-full border border-[#1e3d3a]/12 text-[#1e3d3a]/40 px-2 py-1 text-[10px] hover:text-[#b23a48] hover:border-[#b23a48]/30">
              <Ban className="h-3 w-3" />
            </button>
          )}
        </div>
      </div>

      {expanded && (
        <div className="mt-3 ml-7 space-y-3">
          {founders.length > 0 && (
            <div className="flex items-center gap-2 text-[11px] text-[#1e3d3a]/60" data-testid={`outreach-reassign-${lead.id}`}>
              <span className="uppercase tracking-[0.16em] text-[10px] text-[#1e3d3a]/45">Reaching out</span>
              <select value={lead.owner || ""} onChange={(e) => onReassign?.(lead.id, e.target.value)}
                className="rounded-lg border border-[#1e3d3a]/15 bg-white px-2.5 py-1 text-xs focus:border-[#c9a961] focus:outline-none">
                <option value="">Unassigned</option>
                {founders.map((f) => <option key={f.username} value={f.username}>{f.name}</option>)}
              </select>
              {lead.owner && <span className="text-[10px] text-[#1e3d3a]/40">— credited with this lead</span>}
            </div>
          )}

          {(a.likely_pain_points?.length > 0 || a.elevare_angle || lead.score_reasons?.length > 0) && (
            <div className="rounded-xl border border-[#1e3d3a]/12 bg-[#eae7de]/40 px-4 py-3 text-xs text-[#1e3d3a] space-y-2">
              {lead.score_reasons?.length > 0 && (
                <div><span className="font-semibold text-[#a3823f]">Why this score:</span> {lead.score_reasons.join(" · ")}</div>
              )}
              {a.industry && <div><span className="font-semibold">Industry:</span> {a.industry}</div>}
              {a.likely_pain_points?.length > 0 && (
                <div><span className="font-semibold">Likely pain points:</span>
                  <ul className="ml-4 list-disc mt-0.5">{a.likely_pain_points.map((p, i) => <li key={i}>{p}</li>)}</ul>
                </div>
              )}
              {a.elevare_angle && <div><span className="font-semibold">Elevare angle:</span> {a.elevare_angle}</div>}
            </div>
          )}

          {/* Contact email */}
          <div className="flex flex-wrap items-center gap-2">
            <label className="text-[10px] uppercase tracking-[0.16em] text-[#1e3d3a]/50">Contact email</label>
            <input data-testid={`outreach-email-${lead.id}`} value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="owner@business.com"
              className="flex-1 min-w-[200px] rounded-lg border border-[#1e3d3a]/15 bg-white px-3 py-1.5 text-xs focus:border-[#c9a961] focus:outline-none" />
            {(lead.email_statuses?.[email] || (email && email === lead.contact_email ? lead.email_status : null)) && (
              <span data-testid={`outreach-email-status-${lead.id}`}>
                <EmailBadge status={lead.email_statuses?.[email] || lead.email_status} />
              </span>
            )}
          </div>
          {lead.website && (
            <div className="text-[11px] text-[#1e3d3a]/55">
              Website: <a href={lead.website} target="_blank" rel="noreferrer" className="text-[#a3823f] underline" data-testid={`outreach-website-${lead.id}`}>{lead.website.replace(/^https?:\/\//, "")}</a>
              {lead.phone ? ` · ${lead.phone}` : ""}
            </div>
          )}
          {lead.key_contacts?.length > 0 && (
            <div className="rounded-xl border border-[#1e3d3a]/12 bg-[#eae7de]/40 px-4 py-3 space-y-1.5" data-testid={`outreach-contacts-${lead.id}`}>
              <span className="text-[10px] uppercase tracking-[0.16em] text-[#a3823f]">Decision-makers</span>
              <ul className="space-y-1">
                {lead.key_contacts.map((k, i) => (
                  <li key={i} className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-[#1e3d3a]">
                    <span className="font-semibold">{k.name || "—"}</span>
                    {k.role && <span className="rounded-full bg-[#1e3d3a]/8 px-2 py-0.5 text-[10px] uppercase tracking-[0.1em] text-[#1e3d3a]/60">{k.role}</span>}
                    {k.email && (
                      <button type="button" data-testid={`outreach-contact-email-${lead.id}-${i}`} onClick={() => setEmail(k.email)}
                        className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] transition-colors duration-300 ${email === k.email ? "border-[#6b7c3a]/50 bg-[#6b7c3a]/10 text-[#4d5a24]" : "border-[#1e3d3a]/15 text-[#1e3d3a]/60 hover:border-[#c9a961]"}`}>
                        <Mail className="h-2.5 w-2.5" /> {k.email}
                      </button>
                    )}
                    {k.phone && <span className="inline-flex items-center gap-1 text-[10px] text-[#1e3d3a]/50"><Phone className="h-2.5 w-2.5" /> {k.phone}</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {lead.contact_emails_found?.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5" data-testid={`outreach-emails-found-${lead.id}`}>
              <span className="text-[10px] uppercase tracking-[0.14em] text-[#1e3d3a]/45">Emails found:</span>
              {lead.contact_emails_found.map((e) => {
                const st = lead.email_statuses?.[e];
                const dot = EMAIL_STATUS_META[st]?.dot;
                return (
                <button key={e} type="button" onClick={() => setEmail(e)}
                  title={st ? `Email looks ${st}` : ""}
                  className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] transition-colors duration-300 ${email === e ? "border-[#6b7c3a]/50 bg-[#6b7c3a]/10 text-[#4d5a24]" : "border-[#1e3d3a]/15 text-[#1e3d3a]/60 hover:border-[#c9a961]"}`}>
                  {dot && <span className={`h-1.5 w-1.5 rounded-full ${dot}`} />}
                  {e}
                </button>
                );
              })}
            </div>
          )}

          {/* Draft editor */}
          {lead.draft ? (
            <div className="rounded-xl border border-[#c9a961]/30 bg-white px-4 py-3 space-y-2">
              <input data-testid={`outreach-subject-${lead.id}`} value={subject} onChange={(e) => setSubject(e.target.value)}
                placeholder="Subject"
                className="w-full rounded-lg border border-[#1e3d3a]/15 bg-white px-3 py-2 text-sm font-medium focus:border-[#c9a961] focus:outline-none" />
              <textarea data-testid={`outreach-body-${lead.id}`} value={body} onChange={(e) => setBody(e.target.value)} rows={9}
                className="w-full rounded-lg border border-[#1e3d3a]/15 bg-white px-3 py-2 text-sm leading-relaxed focus:border-[#c9a961] focus:outline-none whitespace-pre-wrap" />
              <div className="flex items-center gap-2">
                <button type="button" data-testid={`outreach-save-${lead.id}`} onClick={saveDraft}
                  className="inline-flex items-center gap-1 rounded-full border border-[#1e3d3a]/15 text-[#1e3d3a]/70 px-3 py-1 text-[10px] uppercase tracking-[0.16em] hover:border-[#c9a961] hover:text-[#a3823f]">
                  <Save className="h-3 w-3" /> Save draft
                </button>
                {(lead.status === "queued" || lead.status === "needs_email" || lead.status === "drafted") && (
                  <button type="button" data-testid={`outreach-send-inline-${lead.id}`} onClick={approve} disabled={busy}
                    className="inline-flex items-center gap-1 rounded-full bg-[#1e3d3a] text-white px-3 py-1 text-[10px] uppercase tracking-[0.16em] hover:bg-[#c9a961] hover:text-[#1e3d3a] disabled:opacity-50">
                    {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />} Approve & send
                  </button>
                )}
              </div>
            </div>
          ) : (
            <p className="text-[11px] text-[#1e3d3a]/50">No draft yet — analyze then draft to generate a personalized email.</p>
          )}

          {lead.reply_snippet && (
            <div className="rounded-xl border border-[#6b7c3a]/30 bg-[#6b7c3a]/6 px-4 py-3 text-xs text-[#1e3d3a]">
              <span className="font-semibold text-[#4d5a24]">Their email reply:</span> {lead.reply_snippet}
            </div>
          )}
          {lead.sms_reply_snippet && (
            <div className="rounded-xl border border-[#6b7c3a]/30 bg-[#6b7c3a]/6 px-4 py-3 text-xs text-[#1e3d3a]">
              <span className="font-semibold text-[#4d5a24]">Their text reply:</span> {lead.sms_reply_snippet}
            </div>
          )}

          {/* Reply directly to this prospect */}
          {hasReplied && (
            <div data-testid={`outreach-reply-panel-${lead.id}`} className="rounded-xl border border-[#6b7c3a]/30 bg-[#6b7c3a]/[0.04] px-4 py-3 space-y-2">
              <div className="flex items-center justify-between gap-2">
                <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.16em] text-[#4d5a24]">
                  <Reply className="h-3.5 w-3.5" /> Reply to {lead.business_name}
                </span>
                <span className="inline-flex items-center gap-1 text-[10px] text-[#1e3d3a]/50">
                  via {replyChannel === "sms"
                    ? <><Smartphone className="h-3 w-3" /> text {lead.phone ? `· ${lead.phone}` : ""}</>
                    : <><Mail className="h-3 w-3" /> email {lead.contact_email ? `· ${lead.contact_email}` : ""}</>}
                </span>
              </div>
              <textarea ref={replyRef} data-testid={`outreach-reply-body-${lead.id}`} value={replyBody} onChange={(e) => setReplyBody(e.target.value)} rows={3}
                placeholder={replyChannel === "sms" ? "Write a text reply…" : "Write an email reply…"}
                className="w-full rounded-lg border border-[#1e3d3a]/15 bg-white px-3 py-2 text-sm leading-relaxed focus:border-[#6b7c3a] focus:outline-none whitespace-pre-wrap" />
              <div className="flex items-center gap-2">
                <button type="button" data-testid={`outreach-reply-send-${lead.id}`} onClick={sendLeadReply}
                  disabled={replyBusy || !replyBody.trim() || (replyChannel === "sms" && (!smsConfigured || !lead.phone))}
                  title={replyChannel === "sms" ? (!smsConfigured ? "Connect Twilio to send texts" : !lead.phone ? "No phone number" : "Send text reply") : "Send email reply"}
                  className="inline-flex items-center gap-1.5 rounded-full bg-[#4d5a24] text-white px-4 py-1.5 text-[10px] uppercase tracking-[0.16em] hover:bg-[#6b7c3a] disabled:opacity-50 transition-colors duration-300">
                  {replyBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Send reply
                </button>
                {replyChannel === "sms" && !smsConfigured && <span className="text-[10px] text-[#a3823f]">Connect Twilio to send texts</span>}
                {replyChannel === "email" && !(lead.contact_email || "").includes("@") && <span className="text-[10px] text-[#a3823f]">No email on file</span>}
              </div>
            </div>
          )}
          {lead.followups?.length > 0 && (
            <div className="text-[11px] text-[#1e3d3a]/50">
              {lead.followups.length} follow-up{lead.followups.length > 1 ? "s" : ""} sent{lead.next_followup_at ? ` · next ${new Date(lead.next_followup_at).toLocaleDateString()}` : ""}
            </div>
          )}

          {/* Text message (SMS) */}
          <div className="rounded-xl border border-[#1e3d3a]/12 bg-[#1e3d3a]/[0.03] px-4 py-3 space-y-2" data-testid={`outreach-sms-panel-${lead.id}`}>
            <div className="flex items-center justify-between gap-2">
              <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.16em] text-[#1e3d3a]/60">
                <Smartphone className="h-3.5 w-3.5 text-[#a3823f]" /> Text message
              </span>
              {lead.phone ? (
                <span className="inline-flex items-center gap-1 text-[11px] text-[#1e3d3a]/50"><Phone className="h-3 w-3" /> {lead.phone}</span>
              ) : (
                <span className="text-[11px] text-[#b23a48]">No phone number on file</span>
              )}
            </div>
            {lead.sms_status === "sent" && (
              <p className="text-[11px] text-[#4d5a24]" data-testid={`outreach-sms-sent-note-${lead.id}`}>
                ✓ Sent{lead.sms_sent_at ? ` ${new Date(lead.sms_sent_at).toLocaleDateString()}` : ""}{lead.sms_count > 1 ? ` · ${lead.sms_count}×` : ""}
              </p>
            )}
            <textarea data-testid={`outreach-sms-body-${lead.id}`} value={smsBody} onChange={(e) => setSmsBody(e.target.value)} rows={3}
              placeholder="Draft a text or click Draft to auto-write one…"
              className="w-full rounded-lg border border-[#1e3d3a]/15 bg-white px-3 py-2 text-sm leading-relaxed focus:border-[#c9a961] focus:outline-none whitespace-pre-wrap" />
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] text-[#1e3d3a]/40" data-testid={`outreach-sms-cost-${lead.id}`}>
                {(smsBody || "").length} chars{smsBody ? ` · ${smsSegments(smsBody)} seg · ≈$${smsCostUsd(smsBody).toFixed(4)}` : ""}
              </span>
              <button type="button" data-testid={`outreach-sms-draft-${lead.id}`} onClick={genSms} disabled={smsBusy}
                className="inline-flex items-center gap-1 rounded-full border border-[#1e3d3a]/15 text-[#1e3d3a]/70 px-3 py-1 text-[10px] uppercase tracking-[0.16em] hover:border-[#c9a961] hover:text-[#a3823f] disabled:opacity-50">
                {smsBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />} Draft
              </button>
              <button type="button" data-testid={`outreach-sms-send-${lead.id}`} onClick={sendSms} disabled={smsBusy || !lead.phone || !smsConfigured}
                title={!smsConfigured ? "Connect Twilio to send" : !lead.phone ? "No phone number" : "Send text"}
                className="inline-flex items-center gap-1 rounded-full bg-[#1e3d3a] text-white px-3 py-1 text-[10px] uppercase tracking-[0.16em] hover:bg-[#c9a961] hover:text-[#1e3d3a] disabled:opacity-50">
                {smsBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />} Send text
              </button>
              {!smsConfigured && <span className="text-[10px] text-[#a3823f]">Connect Twilio in settings to enable sending</span>}
            </div>
          </div>
        </div>
      )}
    </li>
  );
}

const DAYS = [
  { v: "daily", label: "Every day" },
  { v: 0, label: "Mondays" }, { v: 1, label: "Tuesdays" }, { v: 2, label: "Wednesdays" },
  { v: 3, label: "Thursdays" }, { v: 4, label: "Fridays" }, { v: 5, label: "Saturdays" }, { v: 6, label: "Sundays" },
];

const TZS = [
  "America/New_York", "America/Chicago", "America/Denver", "America/Phoenix",
  "America/Los_Angeles", "America/Anchorage", "Pacific/Honolulu", "UTC",
];

function ScheduleEditor({ preset, onSave, onCancel }) {
  const s = preset.schedule || {};
  const browserTz = (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return "UTC"; } })();
  const [enabled, setEnabled] = useState(!!s.enabled);
  const [day, setDay] = useState(s.day === undefined ? "daily" : s.day);
  const [tz, setTz] = useState(s.tz || browserTz || "UTC");
  const [time, setTime] = useState(
    `${String(s.hour ?? 9).padStart(2, "0")}:${String(s.minute ?? 0).padStart(2, "0")}`
  );
  const tzOptions = TZS.includes(tz) ? TZS : [tz, ...TZS];

  const submit = () => {
    const [h, m] = time.split(":").map((x) => parseInt(x, 10));
    onSave(preset.id, {
      enabled,
      day: day === "daily" ? "daily" : Number(day),
      hour: isNaN(h) ? 9 : h,
      minute: isNaN(m) ? 0 : m,
      tz,
    });
  };

  return (
    <div data-testid={`outreach-schedule-editor-${preset.id}`}
      className="rounded-xl border border-[#6b7c3a]/30 bg-[#6b7c3a]/6 p-4 mb-1 flex flex-wrap items-end gap-3">
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#4d5a24] w-full flex items-center gap-1.5">
        <Clock className="h-3.5 w-3.5" /> Auto-run “{preset.name}”
      </div>
      <label className="flex items-center gap-2 text-xs text-[#1e3d3a]/70">
        <input type="checkbox" data-testid={`outreach-schedule-enabled-${preset.id}`} checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
        Enabled
      </label>
      <div>
        <label className="block text-[10px] uppercase tracking-[0.16em] text-[#1e3d3a]/50 mb-1">Day</label>
        <select data-testid={`outreach-schedule-day-${preset.id}`} value={day} onChange={(e) => setDay(e.target.value)}
          className="rounded-lg border border-[#1e3d3a]/15 bg-white px-3 py-1.5 text-sm focus:border-[#c9a961] focus:outline-none">
          {DAYS.map((d) => <option key={String(d.v)} value={d.v}>{d.label}</option>)}
        </select>
      </div>
      <div>
        <label className="block text-[10px] uppercase tracking-[0.16em] text-[#1e3d3a]/50 mb-1">Time</label>
        <input type="time" data-testid={`outreach-schedule-time-${preset.id}`} value={time} onChange={(e) => setTime(e.target.value)}
          className="rounded-lg border border-[#1e3d3a]/15 bg-white px-3 py-1.5 text-sm focus:border-[#c9a961] focus:outline-none" />
      </div>
      <div>
        <label className="block text-[10px] uppercase tracking-[0.16em] text-[#1e3d3a]/50 mb-1">Timezone</label>
        <select data-testid={`outreach-schedule-tz-${preset.id}`} value={tz} onChange={(e) => setTz(e.target.value)}
          className="rounded-lg border border-[#1e3d3a]/15 bg-white px-3 py-1.5 text-sm focus:border-[#c9a961] focus:outline-none">
          {tzOptions.map((z) => <option key={z} value={z}>{z.replace("_", " ")}</option>)}
        </select>
      </div>
      <button type="button" data-testid={`outreach-schedule-save-${preset.id}`} onClick={submit}
        className="inline-flex items-center gap-1 rounded-full bg-[#1e3d3a] text-white px-4 py-1.5 text-[10px] uppercase tracking-[0.16em] hover:bg-[#c9a961] hover:text-[#1e3d3a] transition-colors duration-300">
        <Save className="h-3 w-3" /> Save schedule
      </button>
      <button type="button" onClick={onCancel}
        className="text-[11px] text-[#1e3d3a]/50 hover:text-[#b23a48]">Cancel</button>
    </div>
  );
}


function MassSendScheduler({ schedule, smsConfigured, onSave, onClose }) {
  const s = schedule || {};
  const browserTz = (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return "UTC"; } })();
  const [enabled, setEnabled] = useState(!!s.enabled);
  const [channel, setChannel] = useState(s.channel || "email");
  const [day, setDay] = useState(s.day === undefined ? "daily" : s.day);
  const [tz, setTz] = useState(s.tz || browserTz || "UTC");
  const [time, setTime] = useState(`${String(s.hour ?? 9).padStart(2, "0")}:${String(s.minute ?? 0).padStart(2, "0")}`);
  const tzOptions = TZS.includes(tz) ? TZS : [tz, ...TZS];

  const submit = () => {
    const [h, m] = time.split(":").map((x) => parseInt(x, 10));
    onSave({
      enabled, channel,
      day: day === "daily" ? "daily" : Number(day),
      hour: isNaN(h) ? 9 : h, minute: isNaN(m) ? 0 : m, tz,
    });
    onClose?.();
  };

  return (
    <div data-testid="outreach-mass-scheduler" className="mt-3 rounded-xl border border-[#1e3d3a]/12 bg-white p-4 flex flex-wrap items-end gap-3">
      <div className="w-full text-[10px] uppercase tracking-[0.2em] text-[#1e3d3a]/55 flex items-center gap-1.5">
        <Clock className="h-3.5 w-3.5 text-[#a3823f]" /> Schedule a recurring blast
      </div>
      <label className="flex items-center gap-2 text-xs text-[#1e3d3a]/70">
        <input type="checkbox" data-testid="outreach-mass-sched-enabled" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
        Enabled
      </label>
      <div>
        <label className="block text-[10px] uppercase tracking-[0.16em] text-[#1e3d3a]/50 mb-1">Send</label>
        <select data-testid="outreach-mass-sched-channel" value={channel} onChange={(e) => setChannel(e.target.value)}
          className="rounded-lg border border-[#1e3d3a]/15 bg-white px-3 py-1.5 text-sm focus:border-[#c9a961] focus:outline-none">
          <option value="email">All emails</option>
          <option value="sms">All texts{smsConfigured ? "" : " (needs Twilio)"}</option>
        </select>
      </div>
      <div>
        <label className="block text-[10px] uppercase tracking-[0.16em] text-[#1e3d3a]/50 mb-1">Day</label>
        <select data-testid="outreach-mass-sched-day" value={day} onChange={(e) => setDay(e.target.value)}
          className="rounded-lg border border-[#1e3d3a]/15 bg-white px-3 py-1.5 text-sm focus:border-[#c9a961] focus:outline-none">
          {DAYS.map((d) => <option key={String(d.v)} value={d.v}>{d.label}</option>)}
        </select>
      </div>
      <div>
        <label className="block text-[10px] uppercase tracking-[0.16em] text-[#1e3d3a]/50 mb-1">Time (local)</label>
        <input type="time" data-testid="outreach-mass-sched-time" value={time} onChange={(e) => setTime(e.target.value)}
          className="rounded-lg border border-[#1e3d3a]/15 bg-white px-3 py-1.5 text-sm focus:border-[#c9a961] focus:outline-none" />
      </div>
      <div>
        <label className="block text-[10px] uppercase tracking-[0.16em] text-[#1e3d3a]/50 mb-1">Timezone</label>
        <select data-testid="outreach-mass-sched-tz" value={tz} onChange={(e) => setTz(e.target.value)}
          className="rounded-lg border border-[#1e3d3a]/15 bg-white px-3 py-1.5 text-sm focus:border-[#c9a961] focus:outline-none">
          {tzOptions.map((z) => <option key={z} value={z}>{z.replace("_", " ")}</option>)}
        </select>
      </div>
      <button type="button" data-testid="outreach-mass-sched-save" onClick={submit}
        className="inline-flex items-center gap-1 rounded-full bg-[#1e3d3a] text-white px-4 py-1.5 text-[10px] uppercase tracking-[0.16em] hover:bg-[#c9a961] hover:text-[#1e3d3a] transition-colors duration-300">
        <Save className="h-3 w-3" /> Save schedule
      </button>
      <button type="button" onClick={onClose} className="text-[11px] text-[#1e3d3a]/50 hover:text-[#b23a48]">Cancel</button>
    </div>
  );
}

const fmtTime = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return ""; }
};

function InboxPanel({ data, activeId, onSelect, replyText, setReplyText, onSend }) {
  const convos = data?.conversations || [];
  const active = convos.find((c) => c.id === activeId) || null;
  return (
    <div data-testid="outreach-inbox-panel" className="rounded-2xl border border-[#1e3d3a]/12 bg-white overflow-hidden">
      <div className="px-5 py-3 border-b border-[#1e3d3a]/10 flex items-center gap-2">
        <Inbox className="h-4 w-4 text-[#a3823f]" />
        <span className="text-[11px] uppercase tracking-[0.2em] text-[#1e3d3a]/65">Text inbox</span>
        <span className="text-[11px] text-[#1e3d3a]/40">{convos.length} conversation{convos.length === 1 ? "" : "s"}</span>
      </div>
      {convos.length === 0 ? (
        <p data-testid="outreach-inbox-empty" className="text-xs text-[#1e3d3a]/55 py-8 text-center">
          No text conversations yet. Send a text from a lead and replies will land here.
        </p>
      ) : (
        <div className="grid md:grid-cols-[260px_minmax(0,1fr)] min-h-[280px]">
          {/* Conversation list */}
          <ul className="border-b md:border-b-0 md:border-r border-[#1e3d3a]/10 max-h-[420px] overflow-y-auto divide-y divide-[#1e3d3a]/6">
            {convos.map((c) => (
              <li key={c.id}>
                <button type="button" data-testid={`outreach-convo-${c.id}`} onClick={() => onSelect(c.id)}
                  className={`w-full text-left px-4 py-3 transition-colors duration-200 ${active?.id === c.id ? "bg-[#c9a961]/12" : "hover:bg-[#1e3d3a]/4"}`}>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-[#1e3d3a] truncate flex-1">{c.business_name}</span>
                    {c.unread && <span className="h-2 w-2 rounded-full bg-[#b23a48]" title="New reply" />}
                    {c.status === "opted_out" && <span className="text-[9px] uppercase text-[#b23a48]">opted out</span>}
                  </div>
                  <div className="text-[11px] text-[#1e3d3a]/50 truncate">{c.phone}</div>
                  <div className="text-[10px] text-[#1e3d3a]/35">{fmtTime(c.last_at)}</div>
                </button>
              </li>
            ))}
          </ul>
          {/* Thread */}
          <div className="flex flex-col">
            {!active ? (
              <div className="flex-1 flex items-center justify-center text-xs text-[#1e3d3a]/45 py-10">Select a conversation to read the thread.</div>
            ) : (
              <>
                <div className="px-5 py-2.5 border-b border-[#1e3d3a]/8 flex items-center gap-2">
                  <Smartphone className="h-3.5 w-3.5 text-[#a3823f]" />
                  <span className="text-sm font-medium text-[#1e3d3a]">{active.business_name}</span>
                  <span className="text-[11px] text-[#1e3d3a]/45">{active.phone}</span>
                </div>
                <div data-testid="outreach-thread" className="flex-1 max-h-[340px] overflow-y-auto px-4 py-3 space-y-2 bg-[#eae7de]/30">
                  {(active.thread || []).map((m, i) => (
                    <div key={i} className={`flex ${m.dir === "out" ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-sm leading-snug ${m.dir === "out" ? "bg-[#1e3d3a] text-white rounded-br-sm" : "bg-white border border-[#1e3d3a]/12 text-[#1e3d3a] rounded-bl-sm"}`}>
                        <div className="whitespace-pre-wrap">{m.body}</div>
                        <div className={`mt-0.5 text-[9px] ${m.dir === "out" ? "text-white/50" : "text-[#1e3d3a]/40"}`}>{fmtTime(m.at)}</div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="border-t border-[#1e3d3a]/10 p-3 flex items-end gap-2">
                  <textarea data-testid="outreach-reply-input" value={replyText} onChange={(e) => setReplyText(e.target.value)} rows={2}
                    placeholder={active.status === "opted_out" ? "This lead opted out — replies are disabled." : (data.sms_configured ? "Type a reply…" : "Connect Twilio to send replies…")}
                    disabled={active.status === "opted_out"}
                    className="flex-1 rounded-lg border border-[#1e3d3a]/15 bg-white px-3 py-2 text-sm focus:border-[#c9a961] focus:outline-none disabled:opacity-50" />
                  <button type="button" data-testid="outreach-reply-send" onClick={onSend}
                    disabled={!replyText.trim() || !data.sms_configured || active.status === "opted_out"}
                    title={!data.sms_configured ? "Connect Twilio to send" : "Send reply"}
                    className="inline-flex items-center gap-1 rounded-full bg-[#1e3d3a] text-white px-4 py-2 text-[10px] uppercase tracking-[0.16em] hover:bg-[#c9a961] hover:text-[#1e3d3a] disabled:opacity-50 transition-colors duration-300">
                    <SendIcon className="h-3.5 w-3.5" /> Send
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function AnalyticsPanel({ data, onSetGoal }) {
  if (!data) return (
    <div className="flex items-center gap-2 text-sm text-[#1e3d3a]/55 py-6 px-5 rounded-2xl border border-[#1e3d3a]/12 bg-white" data-testid="outreach-analytics-panel">
      <Loader2 className="h-4 w-4 animate-spin" /> Loading analytics…
    </div>
  );
  const Section = ({ title, icon: Icon, block }) => (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-[#a3823f]" />
        <span className="text-[11px] uppercase tracking-[0.2em] text-[#1e3d3a]/65">{title}</span>
      </div>
      {block.ab && (
        <div data-testid="outreach-ab-verdict" className={`rounded-lg border px-3 py-2 text-xs flex items-start gap-2 ${block.ab.status === "decided" ? "border-[#6b7c3a]/40 bg-[#6b7c3a]/8 text-[#4d5a24]" : "border-[#c9a961]/40 bg-[#c9a961]/8 text-[#a3823f]"}`}>
          <Award className="h-4 w-4 shrink-0 mt-0.5" />
          {block.ab.status === "decided" ? (
            <span>Winner: <b>“{block.ab.winner_label}”</b> at {block.ab.winner_reply_rate}% reply rate (vs {block.ab.loser_reply_rate}%).</span>
          ) : (
            <span>{block.ab.message}</span>
          )}
        </div>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {[
          { k: "contacted", label: "Contacted", v: block.totals.contacted, c: "text-[#1e3d3a]" },
          { k: "reply_rate", label: "Reply rate", v: `${block.totals.reply_rate}%`, c: "text-[#4d5a24]" },
          { k: "positive_rate", label: "Positive", v: `${block.totals.positive_rate}%`, c: "text-[#4d5a24]" },
          { k: "opt_out_rate", label: "Opt-out rate", v: `${block.totals.opt_out_rate}%`, c: "text-[#b23a48]" },
        ].map((s) => (
          <div key={s.k} className="rounded-xl border border-[#1e3d3a]/12 bg-white px-3 py-2.5">
            <div className={`font-head text-xl font-light tabular-nums ${s.c}`}>{s.v}</div>
            <div className="text-[10px] uppercase tracking-[0.14em] text-[#1e3d3a]/50">{s.label}</div>
          </div>
        ))}
      </div>
      {block.templates.length === 0 ? (
        <p className="text-[11px] text-[#1e3d3a]/45">No sends on this channel yet.</p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-[#1e3d3a]/10">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-[#1e3d3a]/4 text-[#1e3d3a]/55 text-[10px] uppercase tracking-[0.1em]">
                <th className="text-left px-3 py-2 font-medium">Message</th>
                <th className="px-2 py-2 font-medium">Sent</th>
                <th className="px-2 py-2 font-medium">Reply %</th>
                <th className="px-2 py-2 font-medium">Positive %</th>
                <th className="px-2 py-2 font-medium">Opt-out %</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#1e3d3a]/6">
              {block.templates.map((t) => (
                <tr key={t.key} data-testid={`outreach-analytics-row-${t.key}`}>
                  <td className="px-3 py-2 text-[#1e3d3a] max-w-[260px] truncate" title={t.label}>{t.label}</td>
                  <td className="px-2 py-2 text-center tabular-nums text-[#1e3d3a]/70">{t.contacted}</td>
                  <td className="px-2 py-2 text-center tabular-nums text-[#4d5a24]">{t.reply_rate}%</td>
                  <td className="px-2 py-2 text-center tabular-nums text-[#4d5a24]">{t.positive_rate}%</td>
                  <td className="px-2 py-2 text-center tabular-nums text-[#b23a48]">{t.opt_out_rate}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
  return (
    <div data-testid="outreach-analytics-panel" className="rounded-2xl border border-[#1e3d3a]/12 bg-white p-5 space-y-6">
      {(data.by_founder?.length > 0) && (
        <div className="space-y-3" data-testid="outreach-leaderboard">
          <div className="flex items-center gap-2">
            <Trophy className="h-4 w-4 text-[#a3823f]" />
            <span className="text-[11px] uppercase tracking-[0.2em] text-[#1e3d3a]/65">Founder leaderboard — who's landing leads</span>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {data.by_founder.map((f) => {
              const winning = data.leader === f.username && f.contacted > 0;
              return (
                <div key={f.username} data-testid={`outreach-founder-${f.username}`}
                  className={`rounded-xl border p-4 ${winning ? "border-[#6b7c3a]/50 bg-[#6b7c3a]/8" : "border-[#1e3d3a]/12 bg-white"}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-head text-lg text-[#1e3d3a]">{f.name}</span>
                    {winning && <span className="inline-flex items-center gap-1 rounded-full bg-[#6b7c3a] text-white px-2 py-0.5 text-[9px] uppercase tracking-[0.14em]"><Trophy className="h-3 w-3" /> Leading</span>}
                  </div>
                  <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                    <div><div className="font-head text-xl text-[#1e3d3a] tabular-nums">{f.contacted}</div><div className="text-[9px] uppercase tracking-[0.12em] text-[#1e3d3a]/50">Contacted</div></div>
                    <div><div className="font-head text-xl text-[#4d5a24] tabular-nums">{f.reply_rate}%</div><div className="text-[9px] uppercase tracking-[0.12em] text-[#1e3d3a]/50">Reply</div></div>
                    <div><div className="font-head text-xl text-[#4d5a24] tabular-nums">{f.converted}</div><div className="text-[9px] uppercase tracking-[0.12em] text-[#1e3d3a]/50">Converted</div></div>
                  </div>
                  <div className="mt-2 text-[10px] text-[#1e3d3a]/50 text-center">{f.positive} positive · {f.meetings} meetings · {f.opted_out} opted out</div>
                  <div className="mt-3 pt-2 border-t border-[#1e3d3a]/8" data-testid={`outreach-goal-${f.username}`}>
                    <div className="flex items-center justify-between text-[10px] text-[#1e3d3a]/55 mb-1">
                      <span className="uppercase tracking-[0.14em]">Monthly goal</span>
                      <span className="flex items-center gap-1">
                        {f.converted_month}/
                        <input type="number" min="0" data-testid={`outreach-goal-input-${f.username}`}
                          defaultValue={f.goal || 0}
                          onBlur={(e) => onSetGoal?.(f.username, e.target.value)}
                          className="w-12 rounded border border-[#1e3d3a]/15 bg-white px-1 py-0.5 text-[11px] text-center focus:border-[#c9a961] focus:outline-none" />
                        converted
                      </span>
                    </div>
                    <div className="h-2 w-full rounded-full bg-[#1e3d3a]/10 overflow-hidden">
                      <div className="h-full rounded-full bg-[#6b7c3a] transition-all duration-500"
                        style={{ width: `${Math.min(100, f.goal_pct)}%` }} />
                    </div>
                    <div className="mt-0.5 text-right text-[9px] text-[#6b7c3a] font-medium">{f.goal_pct}%{f.goal ? "" : " · set a target"}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      <Section title="Email performance" icon={Mail} block={data.email} />
      <Section title="Text (SMS) performance" icon={MessageSquare} block={data.sms} />
    </div>
  );
}

